import axiosOriginal from 'axios';
import JSZip from 'jszip';
import LZString from 'lz-string';
import moment from 'moment';
import { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import cinnectaMetadata from '../constants/Cinnecta.json';
import * as endpoints from '../constants/endpoints';
import onemataMetadata from '../constants/Onemata.json';
import retargetlyMetadata from '../constants/Retargetly.json';
import scopesiMetadata from '../constants/Scopesi.json';
import telefonicaMetadata from '../constants/Telefonica.json';
import { useAuth } from '../containers/UseAuth';
import statsService from '../services/statsService';
import {
    campaignsActions,
    commercialsPersonsActions,
    dashboardActions,
    extraActions,
    newCampaignActions,
    notificationsActions,
    screensActions,
    walletActions,
} from '../store';
import axios from '../utils/axios/axios';
import {
    getCampaignProviderSource,
    serializeCampaignProviderSource,
} from '../utils/campaignProviderSource';
import {
    campaignStatus,
    capitalizeFirstLetter,
    dateToIso8601,
    formatDate,
    formatNumber,
    getResolutionByString,
    guessTimezoneOfBrowser,
    isEmpty,
    lastTwoDecimal,
    numberWithCommas,
    numberWithoutCommas,
    setOtherZone,
    stringToRoleUser,
    stringToTypeUser,
} from '../utils/functions';

import {
    formatCampaignAudienceForSave,
    getDemographicSelectionsFromCampaign,
    thereIsNewVersion,
} from './UseServiceFunctions';

import {
    SCHEDULER_MODE_TO_STRATEGY,
    STRATEGY_TO_SCHEDULER_MODE,
} from '../constants/constants';
import { DEFAULT_FORECAST } from '../constants/forecast';
import { useForecast } from '../contexts/ForecastContext';
import { db } from '../db/db';
import useDispatchNotification from '../hooks/notifications/useDispatchNotification';
import { getScreensPage } from '../services/screensService';
import { newCampaignInitialState } from '../store/newCampaign';

const serviceContext = createContext();
// .

export default function ProvideService({ children }) {
    const service = useProvideService();
    return (
        <serviceContext.Provider value={service}>
            {children}
        </serviceContext.Provider>
    );
}

export const useService = () => {
    return useContext(serviceContext);
};

let debounceCampaignPreview;
const FULL_DAY_SCHEDULE = '1'.repeat(24 * 7);

const hasValidActivePoi = (zones) => {
    if (!Array.isArray(zones)) {
        return false;
    }

    return zones.some((zone) => {
        if (
            (zone?.active !== true && zone?.active !== 1) ||
            !Array.isArray(zone?.results)
        ) {
            return false;
        }

        return zone.results.some((result) => {
            return (
                Number.isFinite(Number(result?.lat)) &&
                Number.isFinite(Number(result?.lng)) &&
                Number.isFinite(Number(result?.radius)) &&
                Number(result.radius) > 0
            );
        });
    });
};

const resolveProximityFilterEnabled = (value, zones) => {
    return (
        (value === true || value === 1 || value === '1') &&
        hasValidActivePoi(zones)
    );
};

const getPersistedProximityFilterEnabled = (campaign) =>
    campaign?.proximityFilterEnabled ?? hasValidActivePoi(campaign?.zones);
const GENERAL_DEMOGRAPHIC_INCOMES = ['low', 'middle', 'high'];

const shouldMirrorGeneralDemographics = (filters = {}) => {
    if (typeof filters !== 'object' || !filters) return false;

    const incomeSet = new Set(filters.income_levels ?? []);
    const hasGenderFilter = Boolean(filters.gender);

    const matchesIncome =
        incomeSet.size === GENERAL_DEMOGRAPHIC_INCOMES.length &&
        GENERAL_DEMOGRAPHIC_INCOMES.every((x) => incomeSet.has(x));

    return (
        matchesIncome &&
        Number(filters.age_from) === 0 &&
        Number(filters.age_to) === 99 &&
        !hasGenderFilter
    );
};

const resolveHoursEnabled = (timing = {}) => {
    if (!timing?.isDayParting) {
        return FULL_DAY_SCHEDULE;
    }

    if (timing.hoursEnabled.length === FULL_DAY_SCHEDULE.length) {
        return timing.hoursEnabled;
    }

    return timing?.hoursEnabled || '';
};

const SUPPORTED_STRATEGIES = [
    'evenly',
    'asap',
    'exclusivity',
    'optimizer',
    'viooh_distribution',
];
const DEFAULT_PACING_STRATEGY = SUPPORTED_STRATEGIES[0];
const supportedStrategySet = new Set(SUPPORTED_STRATEGIES);

const normalizeStrategyValue = (value) => {
    if (typeof value !== 'string') return DEFAULT_PACING_STRATEGY;
    const normalized = value.trim().toLowerCase();
    return supportedStrategySet.has(normalized)
        ? normalized
        : DEFAULT_PACING_STRATEGY;
};

const resolveStrategyFromCampaignPayload = (campaign = {}) => {
    if (typeof campaign?.strategy === 'string') {
        return normalizeStrategyValue(campaign.strategy);
    }

    const schedulerMode = campaign?.scheduler_mode ?? campaign?.schedulerMode;
    const mapped = SCHEDULER_MODE_TO_STRATEGY[schedulerMode];
    if (mapped !== undefined) {
        return normalizeStrategyValue(mapped);
    }

    return campaign?.asap === 1 ? 'asap' : DEFAULT_PACING_STRATEGY;
};

/**
 * Turns a geo selection (regions/cities/areas) into the list of string ids the
 * backend persists. Accepts already selected option objects ({ id, name }) as
 * well as plain ids, so a campaign that was never re-opened in the wizard keeps
 * whatever was stored for it.
 */
const normalizeGeoSelection = (items) =>
    (Array.isArray(items) ? items : [])
        .map((item) => {
            const value =
                item && typeof item === 'object'
                    ? (item.id ?? item.value ?? '')
                    : item;
            return String(value ?? '').trim();
        })
        .filter(Boolean);

/**
 * The backend returns geo selections as a plain array, but legacy campaigns can
 * come back as a PHP serialized string (e.g. "a:0:{}") or null.
 */
const normalizeStoredGeoSelection = (value) =>
    Array.isArray(value) ? value.filter((item) => item != null) : [];

function useProvideService() {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const dashboard1 = useSelector((state) => state.dashboard);
    const newCampaign = useSelector((state) => state.newCampaign);
    const screens = useSelector((state) => state.screens.screens);
    const wallet = useSelector((state) => state.wallet);
    const campaignList = useSelector((state) => state.campaigns);
    const extra = useSelector((state) => state.extra);
    const currencies = wallet.currencies;
    const budgets = wallet.budgets;
    const history = useHistory();
    const auth = useAuth();
    const session = auth.session;
    const userId = auth?.session?.user?.id;
    const userCpmMode = auth?.session?.user?.cpm_mode;

    const [margins, setMargins] = useState(-1);
    const creatives = newCampaign.creatives;
    const { forecast, forecastTable, resetForecast } = useForecast();

    const { successNotification, warningNotification } =
        useDispatchNotification();

    useEffect(() => {
        if (session) {
            getCurrencies();
            getCampaignsPerStatus();
            getBudgets();
            getTimezones();
            getUpdates();
        }
    }, [session]);

    const getData = (response) => {
        return 'data' in response && 'data' in response.data
            ? response.data.data
            : [];
    };

    const normalizeExpectedStats = (rawValue) => {
        if (
            rawValue === null ||
            rawValue === undefined ||
            rawValue === '' ||
            rawValue === '{}'
        ) {
            return {};
        }

        let parsedValue = rawValue;
        if (typeof rawValue === 'string') {
            try {
                parsedValue = JSON.parse(rawValue);
            } catch (error) {
                return {};
            }
        }

        if (
            !parsedValue ||
            typeof parsedValue !== 'object' ||
            Array.isArray(parsedValue)
        ) {
            return {};
        }

        return Object.entries(parsedValue).reduce(
            (accumulator, [screenId, metrics]) => {
                const normalizedScreenId = String(screenId || '').trim();
                if (!normalizedScreenId) {
                    return accumulator;
                }

                const normalizeMetric = (metricValue) => {
                    if (
                        metricValue === '' ||
                        metricValue === null ||
                        metricValue === undefined
                    ) {
                        return null;
                    }

                    const parsedMetricValue = Number(metricValue);
                    return Number.isFinite(parsedMetricValue) &&
                        parsedMetricValue >= 0
                        ? parsedMetricValue
                        : null;
                };

                accumulator[normalizedScreenId] = {
                    impressions: normalizeMetric(metrics?.impressions),
                    impacts: normalizeMetric(metrics?.impacts),
                    spent: normalizeMetric(metrics?.spent),
                };

                return accumulator;
            },
            {}
        );
    };

    const serializeExpectedStats = (expectedStats) => {
        const normalizedExpectedStats = normalizeExpectedStats(expectedStats);
        return Object.keys(normalizedExpectedStats).length
            ? JSON.stringify(normalizedExpectedStats)
            : '{}';
    };

    const normalizeCampaignDaysEnabled = (daysEnabled) => {
        return Array.isArray(daysEnabled) ? daysEnabled : [];
    };

    const normalizeCampaignHoursEnabled = (hoursEnabled) => {
        if (Array.isArray(hoursEnabled)) {
            return hoursEnabled;
        }

        if (
            typeof hoursEnabled === 'string' &&
            /^[01]{168}$/.test(hoursEnabled)
        ) {
            const enabledHours = new Set();

            for (let dayIndex = 0; dayIndex < 7; dayIndex += 1) {
                for (let hourIndex = 0; hourIndex < 24; hourIndex += 1) {
                    const position = dayIndex * 24 + hourIndex;
                    if (hoursEnabled[position] === '1') {
                        enabledHours.add(hourIndex);
                    }
                }
            }

            return Array.from(enabledHours).sort((a, b) => a - b);
        }

        return [];
    };

    const getUpdates = async () => {
        let url = endpoints.getUpdates;
        let version = getData(await axios.get(url));
        if (thereIsNewVersion(version.ver, localStorage.getItem('version'))) {
        }
        localStorage.setItem('version', version.ver);
    };

    const getBudgets = async () => {
        let url = endpoints.getBudgetsV2;

        try {
            if (budgets === null) {
                let budgets = getData(await axios.get(url));
                dispatch(walletActions.setBudgets(budgets.budget));
                return budgets;
            } else {
                return budgets;
            }
        } catch (error) {
            ErrorMessage(error);
            return { '-': 0 };
        }
    };

    const getBudgetsDetails = async () => {
        let url = endpoints.getBudgetsDetailsV2;

        try {
            const budgetsDetails = getData(await axios.get(url));
            const currencies = await getCurrencies();
            budgetsDetails.campaigns = budgetsDetails.campaigns.map(
                (campaign) => {
                    campaign.currency = currencies.find(
                        (currency) => currency.id === campaign.currency_id
                    );
                    return campaign;
                }
            );
            return budgetsDetails;
        } catch (error) {
            ErrorMessage(error);
        }
    };

    const getUserMovements = async () => {
        let url = endpoints.getUserMovements;
        try {
            const userMovements = getData(await axios.get(url));
            return userMovements;
        } catch (error) {
            ErrorMessage(error);
        }
    };

    // `syncDashboard: false` is used by the campaign wizard: it only needs the
    // campaign data, and writing it into the dashboard slice leaks the draft
    // into the individual dashboard of whatever campaign is opened next.
    const getCampaignPreview = async (id, { syncDashboard = true } = {}) => {
        let url = endpoints.getCampaignPreviewV2 + `/${id}`;

        try {
            if (syncDashboard) dispatch(dashboardActions.setIsLoading(true));
            let response = await axios.get(url);

            let campaign = getData(response);
            campaign.days_enabled = normalizeCampaignDaysEnabled(
                campaign.days_enabled
            );
            campaign.hoursEnabledSchedule = normalizeCampaignHoursEnabled(
                campaign.hours_enabled
            );
            if (!campaign.screen && Array.isArray(campaign.screens)) {
                campaign.screen = campaign.screens;
            }
            campaign.expected_stats_targets = normalizeExpectedStats(
                campaign.expected_stats_targets
            );
            campaign.currency = getCurrencyById(campaign.currency_id);
            if (syncDashboard) {
                dispatch(dashboardActions.setCampaign(campaign));
                dispatch(dashboardActions.setIsLoading(false));
            }
            return campaign;
        } catch (error) {
            ErrorMessage(error);
            history.push('/dashboard');
            if (syncDashboard) dispatch(dashboardActions.setIsLoading(false));
        }
    };

    async function setDraftCampaignAutodelete(user, days) {
        let url = endpoints.userSettingsV2;

        try {
            let response = await axios.post(url + user, {
                campaign_autodelete: days,
            });
            return response;
        } catch (error) {
            console.error(error);
        }
    }

    async function getUserSettings(user) {
        let url = endpoints.userSettingsV2;
        try {
            let response = await axios.get(url + user);
            return response;
        } catch (error) {
            console.error(error);
        }
    }

    const getTimezones = async () => {
        let url = endpoints.getTimezonesV2;

        try {
            if (newCampaign.timing.timezones.length > 0) {
                //console.log('timezones already loaded')
                return newCampaign.timing.timezones;
            }

            if (!localStorage.getItem('timezones')) {
                let timezones = getData(await axios.get(url));
                timezones = timezones.map((timezone) => ({
                    ...timezone,
                    value: timezone.id,
                    label: timezone.description,
                }));

                localStorage.setItem('timezones', JSON.stringify(timezones));

                db.timezones.clear();
                db.timezones.bulkPut(timezones);

                dispatch(extraActions.setTimezones(timezones));
                return timezones;
            } else {
                let timezones = JSON.parse(localStorage.getItem('timezones'));
                dispatch(extraActions.setTimezones(timezones));
                return timezones;
            }
        } catch (error) {
            ErrorMessage(error);
        }
    };
    const getStatsByScreens = async (campaignId, from, to) => {
        try {
            from = new Date(from);
            from = formatDate(from);
            to = new Date(to);
            to = formatDate(to);
            let url = `${endpoints.getStats}/screens/campaign/${campaignId}?from=${from}&to=${to}`;
            const response = await axios.get(url);

            return response;
        } catch (e) {
            console.error(e);
        }
    };

    const getCampaignStats = async (campaign, currentPeriod) => {
        let promiseStatsByScreens = statsService.getStatsByScreens(
            campaign.id,
            currentPeriod[0],
            currentPeriod[1]
        );
        let promiseStatsByCampaign = statsService.getStatsByCampaign(
            campaign.id,
            currentPeriod[0],
            currentPeriod[1]
        );
        let promiseStatsByHours = statsService.getStatsByCampaignByHours(
            campaign.id,
            currentPeriod[0],
            currentPeriod[1]
        );

        let [statsByScreens, statsByCampaign, statsByHours] =
            await Promise.allSettled([
                promiseStatsByScreens,
                promiseStatsByCampaign,
                promiseStatsByHours,
            ]);

        // Any of the three requests can be rejected (or answer without stats),
        // so every access below has to tolerate missing data instead of
        // throwing and leaving the dashboard stuck.
        const screensData = statsByScreens?.value?.data ?? {};
        const campaignData = statsByCampaign?.value?.data ?? {};
        const hoursData = statsByHours?.value?.data ?? {};

        const key = Object.keys(hoursData?.stats ?? {})[0] ?? null;
        const symbol = campaign?.currency?.short_name;

        const totals = [];

        let formattedScreens = formatTotals(
            key ? screensData?.totals?.[key] : null,
            symbol
        );
        let formattedCampaign = formatTotals(
            key ? campaignData?.totals?.[key] : null,
            symbol
        );
        let formattedHours = formatTotals(
            key ? hoursData?.totals?.[key] : null,
            symbol
        );

        totals.push(formattedScreens, formattedCampaign, formattedHours);

        let total = key ? campaignData?.totals?.[key] : null;

        dispatch(dashboardActions.setIsLoading(false));

        let byDays = key ? (campaignData?.stats?.[key] ?? []) : [];

        let byHours = key ? (hoursData?.stats?.[key] ?? []) : [];

        let byScreen = key ? (screensData?.stats?.[key] ?? []) : [];

        let bySpent = total ? total.spent : 0;

        let byImpressions = total ? total.impressions : 0;

        let byImpacts = total ? total.impacts : 0;

        let bySpot = total ? total.spot : 0;

        let byEcpm = total ? total.ecpm : 0;

        dispatch(
            dashboardActions.setIndividualCampaignStats({
                days: byDays,
                hours: byHours,
                screen: byScreen,
                spent: bySpent,
                impressions: byImpressions,
                impacts: byImpacts,
                spot: bySpot,
                ecpmAverage: byEcpm,
            })
        );
        const newStats = {
            day: campaignData?.stats,
            hour: hoursData?.stats,
            screen: screensData?.stats,
        };

        return { newStats, totals };
    };

    const forgotPassword = async (email) => {
        try {
            let url = endpoints.forgotPasswordV2;
            await axios.post(url, {
                email: email,
                redirectUrl: process.env.REACT_APP_FRONT_END_URL + 'reset', // In the local environment, this requires a "/" character in "reset" to work. But not in QA.
            });
        } catch (error) {
            ErrorMessage(error);
        }
    };

    const resetPassword = async (token, reEnterPassword, username) => {
        try {
            let url = endpoints.resetPasswordV2;
            await axios.post(url, {
                token: token,
                password: reEnterPassword,
                username: username,
            });

            dispatch(
                notificationsActions.setNotification({
                    type: 'success',
                    title: t(
                        'common.notifications.success.passChangeSuccesfull-title'
                    ),
                    message: t(
                        'common.notifications.success.passChangeSuccesfull-description'
                    ),
                })
            );
        } catch (error) {
            ErrorMessage(error);
        }
    };

    const getCampaignPrediction = async (campaign) => {
        const from = moment(campaign.start_period).format('YYYY-MM-DD HH:mm');
        const to = moment(campaign.end_period).format('YYYY-MM-DD HH:mm');
        let url = `${endpoints.getCampaignStatusPrediction}${campaign.id}`;

        try {
            let prediction = (await axios.get(url)).data;
            if (JSON.stringify(prediction) === '[]')
                dispatch(dashboardActions.setCampaignPrediction({}));
            else dispatch(dashboardActions.setCampaignPrediction(prediction));
            return prediction;
        } catch (e) {
            console.error({ e });
            dispatch(
                notificationsActions.setNotification({
                    type: 'warning',
                    title: t('common.notifications.error.title'),
                    message: t('common.notifications.error.errorData'),
                })
            );
            return null;
        }
    };

    const fetchMoreDataOfCurrentCampaignList = async (page, limit) => {
        dispatch(campaignsActions.setIsLoading(true));
        try {
            const newCampaigns = await refreshCampaignListByStatus(
                campaignList.campaignsShowing.status,
                page,
                limit
            );
            return Array.isArray(newCampaigns) ? newCampaigns : [];
        } catch (err) {
            console.error(
                '[useService] fetchMoreDataOfCurrentCampaignList ERROR',
                err
            );
            return [];
        } finally {
            dispatch(campaignsActions.setIsLoading(false));
        }
    };

    const refreshCampaignListByStatus = async (
        status,
        page = 1,
        limit = process.env.REACT_APP_CAMPAIGNS_PER_PAGE
    ) => {
        if (!status) return [];

        const newCampaigns = await getCampaigns(status, page, limit);
        const safeCampaigns = Array.isArray(newCampaigns) ? newCampaigns : [];

        dispatch(
            campaignsActions.changeCampaigns({
                status,
                page,
                campaigns: safeCampaigns,
            })
        );

        return safeCampaigns;
    };

    const getCampaigns = async (
        status,
        page,
        limit = process.env.REACT_APP_CAMPAIGNS_PER_PAGE,
        search
    ) => {
        try {
            if (status !== '') {
                let url = `${endpoints.getCampaigns}?limit=${limit}&userId=${session.user.id}&status=${status}&page=${page}`;
                if (search) url = `${url}&search=${search}`;

                const response = await axios.get(url);
                const data = response.data;
                const campaigns = data?.data?.campaigns?.data;

                if (!Array.isArray(campaigns)) {
                    console.error('getCampaigns: campaigns is NOT an array', {
                        campaigns,
                    });
                    return [];
                }

                try {
                    formatCampaigns(campaigns);
                    return campaigns;
                } catch (err) {
                    console.error('formatCampaigns threw an error', err);
                    return campaigns;
                }
            } else {
                return [];
            }
        } catch (e) {
            console.error({ e });
            dispatch(
                notificationsActions.setNotification({
                    type: 'warning',
                    title: t('common.notifications.error.title'),
                    message: t('common.notifications.error.errorData'),
                })
            );
        }
    };

    const formatCampaigns = (campaigns) => {
        campaigns.forEach((campaign, i) => {
            try {
                formatCampaign(campaign);
            } catch (err) {
                console.error(
                    'formatCampaign ERROR at index:',
                    i,
                    'campaign id:',
                    campaign?.id
                );
                console.error(
                    'Problematic campaign snapshot:',
                    JSON.stringify(campaign).slice(0, 2000)
                );
                console.error('Error:', err);
                throw err;
            }
        });
    };

    const formatCampaign = (campaign) => {
        try {
            const formatNumber = (number) =>
                number !== undefined && number !== null && number !== 0
                    ? numberWithCommas(number.toFixed(2))
                    : '0';

            campaign.auto_distribution =
                campaign.auto_distribution === null
                    ? 0
                    : campaign.auto_distribution;

            campaign.daily_limit = parseInt(campaign.daily_limit);

            campaign.budget =
                typeof campaign.budget === 'string'
                    ? parseInt(numberWithoutCommas(campaign.budget))
                    : campaign.budget;

            campaign.name =
                campaign.name || t('sections.campaign-list.without-name');

            campaign.brand =
                campaign.brand || t('sections.campaign-list.without-brand');

            campaign.dailyCapType =
                campaign.limit_type === 1
                    ? t(
                          'sections.campaigns.new-campaign.wizard-basics.dailyCap.budget-label'
                      )
                    : t(
                          'sections.campaigns.new-campaign.wizard-basics.dailyCap.impressions-label'
                      );

            campaign.spent = formatNumber(campaign.spent);
            campaign.totalSpent = formatNumber(campaign.totalSpent);

            campaign.impressions = formatNumber(campaign.impressions);
            campaign.totalImpressions = formatNumber(campaign.totalImpressions);

            campaign.isDailyCap = !(
                campaign.auto_distribution === 0 && campaign.daily_limit === 0
            );

            campaign.daily_limit = formatNumber(parseInt(campaign.daily_limit));

            campaign.dailyProgress = -1;
            if (campaign.isDailyCap && parseInt(campaign.daily_limit) !== 0) {
                let divisor = 0;
                const impressions = campaign.impressions
                    ? parseInt(numberWithoutCommas(campaign.impressions))
                    : 0;
                const spent = campaign.spent
                    ? parseInt(numberWithoutCommas(campaign.spent))
                    : 0;
                const dailyLimit = parseInt(
                    numberWithoutCommas(campaign.daily_limit)
                );
                divisor = campaign.limit_type === 1 ? spent : impressions;
                campaign.dailyProgress = (divisor / dailyLimit) * 100;
                campaign.dailyProgress = campaign.dailyProgress.toFixed(2);
            }

            campaign.spentProgress = -1;
            if (campaign.totalSpent && campaign.budget > 0) {
                campaign.spentProgress =
                    (numberWithoutCommas(campaign.totalSpent) /
                        numberWithoutCommas(campaign.budget)) *
                    100;
                campaign.spentProgress = campaign.spentProgress.toFixed(2);
            }

            campaign.budget = formatNumber(campaign.budget);
            campaign.isActive = campaign.status === 'ACTIVE';
            campaign.days = null;
            campaign.isTest = campaign.is_test;
        } catch (err) {
            console.error('formatCampaign crashed for:', campaign);
            console.error('Error:', err);
            throw err;
        }
    };

    const onClickNextPageOfCampaignsList = async (pageNumber) => {
        try {
            const nextPage =
                pageNumber > 0
                    ? pageNumber
                    : campaignList.campaignsShowing.page + 1;
            await fetchMoreDataOfCurrentCampaignList(nextPage);
        } catch (error) {
            console.error('Error fetching next page of campaigns:', error);
        }
    };

    const onClickPreviusPageOfCampaignsList = async (pageNumber) => {
        try {
            const previusPage =
                pageNumber > 0
                    ? pageNumber
                    : campaignList.campaignsShowing.page - 1;
            await fetchMoreDataOfCurrentCampaignList(previusPage);
        } catch (error) {
            console.error('Error fetching previus page of campaigns:', error);
        }
    };

    const getCampaignsPerStatus = async () => {
        let url = endpoints.getCampaignsPerStatusV2;
        let statuses = {
            DRAFT: '-',
            JOB_SUCCESS: '-',
            DELETED: '-',
            INACTIVE: '-',
        };

        try {
            const statusesInBack = getData(await axios.get(url));
            statuses = statusesInBack;
        } catch (error) {
            ErrorMessage(error);
        }
        dispatch(campaignsActions.setStatuses(statuses));
        localStorage.setItem('statuses', JSON.stringify(statuses));
        return statuses;
    };

    const getCurrencies = async () => {
        try {
            // NOTE: This needs to be refactored to avoid to mix localStorage
            // and state.
            let newCurrencies = localStorage.getItem('currencies');
            if (!newCurrencies) {
                newCurrencies = getData(
                    await axios.get(endpoints.getCurrenciesV2)
                );
                localStorage.setItem(
                    'currencies',
                    LZString.compress(JSON.stringify(newCurrencies))
                );
                // Clean un currency records
                db.currencies.clear();
                db.currencies.bulkPut(newCurrencies, 'id');
            } else {
                newCurrencies = JSON.parse(LZString.decompress(newCurrencies));
            }

            dispatch(walletActions.setCurrencies(newCurrencies));
            return newCurrencies;
        } catch (error) {
            ErrorMessage(error);
        }
    };

    const getNodes = async (isForFilter = false) => {
        try {
            // tiene que tener paises en extra.location para poder buscar
            if (extra?.location?.countries && isForFilter)
                return extra.location;
            // tiene que tener paises y no por filter
            if (newCampaign.location.nodes.length > 0 && !isForFilter) {
                return newCampaign.location.nodes;
            }
            const nodes = getData(await axios.get(endpoints.getGeoNodesV2));
            const countries = getData(
                await axios.get(endpoints.getGeoCountriesV2)
            );
            const screensForFilter = await getScreens(userId);
            const nodesCountries = nodes.reduce(
                (acc, curr) => [...acc, curr.name],
                []
            );
            countries.forEach((country) => {
                const indexOfCountry = nodesCountries.indexOf(country.code2);
                if (indexOfCountry !== -1) {
                    nodes[indexOfCountry] = {
                        ...nodes[indexOfCountry],
                        label: country.name,
                    };
                }
            });

            let optionCountries = [];
            let optionNodes = [];
            nodes.forEach((node) => {
                let isCountry = false;
                screensForFilter.forEach((screen) => {
                    if (screen?.geo?.country == node?.name) {
                        isCountry = true;
                    }
                });
                if (isCountry) {
                    optionCountries.push({
                        value: node.name,
                        label: node.label,
                    });
                    optionNodes.push(node);
                }
            });
            if (optionCountries.length === 0) {
                const dummyLocation = cloneDummyArgentinaLocation();
                optionCountries = dummyLocation.countries;
                optionNodes = dummyLocation.nodes;
            }
            const newLocation = Object.assign({}, newCampaign.location);
            newLocation.nodes = optionNodes;
            newLocation.countries = optionCountries;
            newLocation.selectedCountries =
                newLocation.selectedCountries.filter((country) =>
                    optionCountries.some(
                        (option) => option.value === country.value
                    )
                );
            dispatch(extraActions.setLocation(newLocation));
            dispatch(newCampaignActions.setLocation(newLocation));
            if (isForFilter) return newLocation;
            else return newLocation.nodes;
        } catch (error) {
            ErrorMessage(error);
            const dummyLocation = cloneDummyArgentinaLocation();
            const newLocation = Object.assign({}, newCampaign.location, {
                nodes: dummyLocation.nodes,
                countries: dummyLocation.countries,
                selectedCountries: [],
            });
            dispatch(extraActions.setLocation(newLocation));
            dispatch(newCampaignActions.setLocation(newLocation));
            return isForFilter ? newLocation : newLocation.nodes;
        }
    };

    const setSelectedCountries = (newCountries) => {
        const moreCountries =
            newCountries.length > newCampaign.location.countries.length;

        const countriesNames = newCountries.map((c) => c.value);
        const includesOnCountriesByCurrency =
            newCampaign.location.selectedCountries.every((sc) => {
                return countriesNames.includes(sc.value);
            });

        const countriesForSelect =
            newCampaign.location.selectedCountries.length &&
            includesOnCountriesByCurrency &&
            moreCountries === false
                ? newCampaign.location.selectedCountries
                : newCountries;
        dispatch(newCampaignActions.setSelectedCountries(countriesForSelect));
    };

    const normalizeScreen = (s) => {
        return {
            ...s,
            key: s.id,
            size:
                s.width != null && s.height != null
                    ? `${s.width}x${s.height}`
                    : undefined,
            lat: s.geo?.lat ?? null,
            lng: s.geo?.lon ?? null,
            isSelected: false,
            label: s.alias || s.name || externalId || s.id,
        };
    };

    const getScreens = async (userId) => {
        try {
            if (typeof db !== 'undefined' && db?.screens?.clear) {
                await db.screens.clear();
            }

            const { data } = await axiosOriginal.get(
                `${endpoints.screensWithSpotCost}?userId=${encodeURIComponent(
                    userId
                )}`
            );

            const rawScreens = Array.isArray(data)
                ? data
                : Array.isArray(data?.message)
                  ? data.message
                  : Array.isArray(data?.data)
                    ? data.data
                    : [];

            const screens = rawScreens.map(normalizeScreen);

            return screens;
        } catch (e) {
            console.error('getScreens failed:', e);
            dispatch(
                notificationsActions.setNotification({
                    type: 'warning',
                    title: t('common.notifications.error.title'),
                    message: t('common.notifications.error.errorData'),
                })
            );
            return [];
        }
    };

    const getScreensReports = async (
        provider = 'onemata',
        userId,
        timezone,
        currency,
        screenId
    ) => {
        const params = new URLSearchParams({
            provider,
            timezone,
            userid: userId,
        });
        // Never serialize an unresolved currency as "undefined" (TGF-1240).
        if (currency) params.set('currency', currency);
        params.set('screenId', screenId);
        const screens = await axiosOriginal.get(
            `${endpoints.screensReportsData}?${params.toString()}`
        );
        return screens;
    };

    const getScreensReportsWithCpm = async (
        provider = 'onemata',
        userId,
        localCurrency
    ) => {
        const medianCpms = await axiosOriginal.get(
            `${endpoints.screensReportsWithCpm}?provider=${provider}&userId=${userId}&localCurrency=${localCurrency}`
        );
        return medianCpms;
    };

    /**
     *
     * @deprecated

     */
    const updateCPMOfScreens = async (screensParams) => {
        if (screensParams === undefined) {
            screensParams = screens;
        }

        const screensWithCpm = screensParams.map((screen) => ({
            ...screen,
        }));

        dispatch(screensActions.setScreens(screensWithCpm));
        return screensWithCpm;
    };

    //Example input = [{"id" : 1}, {"id" : 2}, {"id" : 3}], "id" output = 1,2,3
    const concatItemsByComa = (items, attribute) => {
        let concatenated = '';
        items.forEach((item) => {
            concatenated = concatenated.concat(',', item[attribute]);
        });

        return concatenated.substring(1);
    };

    const getScreenProviders = async () => {
        try {
            const screenProviders = getData(
                await axios.get(endpoints.getProviders)
            );
            return screenProviders.data;
        } catch (e) {
            dispatch(
                notificationsActions.setNotification({
                    type: 'warning',
                    title: t('common.notifications.error.title'),
                    message: t('common.notifications.error.errorData'),
                })
            );
        }
    };

    const getScreenProvidersFiltered = async (countries, regions, cities) => {
        try {
            let url = endpoints.getProviders;
            if (countries.length > 0) {
                url = `${url}?countries=${concatItemsByComa(countries, 'id')}`;
            }

            if (regions.length > 0) {
                url = `${url}&regions=${concatItemsByComa(regions, 'id')}`;
            }

            if (cities.length > 0) {
                url = `${url}&cities=${concatItemsByComa(cities, 'id')}`;
            }
            const screenProviders = getData(await axios.get(url));
            return screenProviders.data;
        } catch (e) {
            console.error({ e });
            dispatch(
                notificationsActions.setNotification({
                    type: 'warning',
                    title: t('common.notifications.error.title'),
                    message: t('common.notifications.error.errorData'),
                })
            );
        }
    };

    const saveCampaignInLocalStorage = (id) => {
        const campaignId = id ?? newCampaign.id;
        if (!campaignId) {
            return;
        }
        const campaign = JSON.parse(JSON.stringify(newCampaign));
        campaign.id = campaignId;
        const forecastSnapshot = JSON.parse(
            JSON.stringify(forecast || DEFAULT_FORECAST)
        );
        forecastSnapshot.schedules = forecastSnapshot.schedules || [];
        forecastSnapshot.impressionsPerScreen =
            forecastSnapshot.impressionsPerScreen || [];
        campaign.forecast = forecastSnapshot;
        campaign.forecastTable = Array.isArray(forecastTable)
            ? [...forecastTable]
            : [];
        localStorage.setItem(
            'newCampaignData',
            LZString.compress(JSON.stringify(campaign))
        );
        localStorage.setItem(
            'currencies',
            LZString.compress(JSON.stringify(currencies))
        );

        // HACK: fix to replicate the behavior of the old localStorage
        db.newCampaignData.clear().then(() => {
            db.newCampaignData.put(campaign, 'id');
        });
    };

    const clearNewCampaignData = () => {
        localStorage.removeItem('newCampaignData');
        // Only clear the localStorage if passed more than 1 hour
        if (Date.now() - newCampaign.timestamp > 3600000) {
            localStorage.removeItem('screens');
        }
        resetForecast();
        dispatch(newCampaignActions.reset());
        dispatch(screensActions.resetByKey('selectedScreensLocal'));
        dispatch(screensActions.resetByKey('isFilterByAudiences'));
        dispatch(screensActions.resetByKey('showSelectedScreens'));
        dispatch(screensActions.resetByKey('selectedScreenTypes'));
        dispatch(screensActions.resetByKey('selectedPublishers'));
        dispatch(screensActions.resetByKey('selectedTiers'));
        dispatch(screensActions.resetByKey('maxCpm'));
    };

    const getCampaignInLocalStorage = () => {
        return JSON.parse(
            LZString.decompress(localStorage.getItem('newCampaignData'))
        );
    };

    const formatValuesSavePreview = (campaign) => {
        const currencyDefault = JSON.parse(
            localStorage.getItem('currencyDefault')
        );
        // si la url tiene la palabra new se setea la currencyDefault
        // if (window.location.href.includes("new")) {
        //     campaign.selectedCurrency = currencyDefault;
        // }

        const request = {};
        const user = session.user;
        const audienceFields = formatCampaignAudienceForSave(campaign);
        request['id'] = campaign.id;
        request['agency'] =
            user && user.company ? user.company : 'Undefined Company';
        request['user_id'] = user.id;
        /* W1 Basics */
        request['name'] = campaign.name;
        request['brand'] = campaign.brand;
        request['budget'] = campaign.budget.budget;
        request['currency_id'] = campaign.selectedCurrency
            ? campaign.selectedCurrency.id
            : currencyDefault.id;
        request['cpm_optimize'] = campaign.budget.cpmOptimizer ? 1 : 0;
        request['timezone_id'] = campaign.timing.selectedTimezone.id;
        request['max_cpm'] = campaign.budget.cpm;
        // TODO:  finish_when_budget is the same to limit_type (delete from front and back)
        request['finish_when_budget'] = campaign.dailyCap.isBudget ? 1 : 0;
        request['limit_type'] = campaign.dailyCap.isBudget ? 1 : 0;
        request['hours_enabled'] = resolveHoursEnabled(campaign.timing);
        const timingStrategy = normalizeStrategyValue(
            campaign.timing?.strategy
        );
        request['asap'] = timingStrategy === 'asap' ? 1 : 0;
        request['scheduler_mode'] = STRATEGY_TO_SCHEDULER_MODE[timingStrategy];
        if (campaign.timing.periodValue) {
            request['start_period'] = campaign.timing.periodValue[0];
            request['end_period'] =
                request['start_period'] === campaign.timing.periodValue[1]
                    ? campaign.timing.periodValue[1] + 1
                    : campaign.timing.periodValue[1];
        } else {
            request['start_period'] = '';
            request['end_period'] = '';
        }

        request['is_day_parting'] = campaign.timing.isDayParting;

        //TODO: We have a matrix, needs update backend to send matrix of hours and days
        if (campaign.dailyCap.isActive) {
            if (campaign.dailyCap.isDistribution) {
                request['auto_distribution'] = 1;
                request['daily_limit'] = 0;
            } else {
                request['auto_distribution'] = 0;
                request['daily_limit'] = campaign.dailyCap.isBudget
                    ? campaign.dailyCap.budget
                    : campaign.dailyCap.impressions;
            }
        } else {
            request['auto_distribution'] = 0;
            request['daily_limit'] = 0;
        }

        // Per-day budget caps: {'YYYY-MM-DD': number}. Empty object clears them.
        request['daily_caps'] =
            buildDailyCapsPayload(
                campaign.dailyCaps,
                campaign.timing.periodValue?.[0],
                campaign.timing.periodValue?.[1]
            ) ?? {};

        /* W2 Location */
        const campaignLocation = campaign?.location || {};
        const selectedCountries = campaignLocation.selectedCountries || [];
        request['countries_selected'] = selectedCountries.length
            ? selectedCountries
                  .map((country) =>
                      String(
                          typeof country === 'string'
                              ? country
                              : country?.iso2 ||
                                    country?.value ||
                                    country?.code2 ||
                                    country?.country_code ||
                                    ''
                      )
                          .trim()
                          .toUpperCase()
                  )
                  .filter((code) => /^[A-Z]{2}$/.test(code))
            : [];

        request['regions_selected'] = normalizeGeoSelection(
            campaignLocation.selectedRegions
        );

        request['cities_selected'] = normalizeGeoSelection(
            campaignLocation.selectedCities
        );

        request['neighborhoods_selected'] = normalizeGeoSelection(
            campaignLocation.selectedNeighborhoods
        );

        const zones = Array.isArray(campaignLocation?.zones)
            ? campaignLocation.zones
            : [];
        const proximityFilterEnabled = resolveProximityFilterEnabled(
            campaign.proximityFilterEnabled,
            zones
        );
        request['zones'] = zones.map((zone) => ({
            ...zone,
            active: proximityFilterEnabled ? zone.active : false,
        }));
        request['proximityFilterEnabled'] = proximityFilterEnabled;

        /* W3 Screens */
        request['screen_providers'] = serializeCampaignProviderSource(
            campaign.providerSource
        );
        if (campaign.selectedScreens.length > 0)
            request['screens'] = campaign.selectedScreens.map(
                (screen) => screen.id
            );

        /* W4 Audiences */
        request['scopesi_ages'] = audienceFields.scopesi_ages;
        request['scopesi_gender'] = audienceFields.scopesi_gender;
        request['scopesi_nse'] = audienceFields.scopesi_nse;
        request['scopesi_coverage'] = campaign.segments.accuracy.sliderValue;

        /* W5 Creatives */
        request['ads_basename'] = campaign.brand;
        request['pixel_tracker_script'] = campaign.tracking.isActive
            ? campaign.tracking.externalTracking
            : '';
        request['creative_categories'] = campaign.selectedCreativeCategories;
        request['provider_audience'] = audienceFields.provider_audience;
        request['is_test'] = campaign.isTest;
        request['expected_stats_targets'] = serializeExpectedStats(
            campaign.expectedStats
        );

        const targetImpacts = campaign?.forecast?.targetImpacts ?? 0;
        request['target_impacts_launch'] = isNaN(targetImpacts)
            ? 0
            : targetImpacts;
        return request;
    };

    const saveOrUpdateCampaignPreview = async (
        basics,
        location,
        screens,
        campaignOverride = null
    ) => {
        const campaignSeed = campaignOverride
            ? { ...campaignOverride }
            : newCampaign;

        const campaignSeedLocation = campaignSeed?.location ?? {};
        const effectiveLocationInput = location ?? {};
        const effectiveLocation = {
            ...campaignSeedLocation,
            ...effectiveLocationInput,
            zones: Array.isArray(effectiveLocationInput?.zones)
                ? effectiveLocationInput.zones
                : Array.isArray(campaignSeedLocation?.zones)
                  ? campaignSeedLocation.zones
                  : [],
        };

        const selectedScreensOverride =
            screens?.selectedScreens ??
            (screens ? screens : campaignSeed?.selectedScreens) ??
            [];

        const dataBeforeFormat = {
            ...campaignSeed,
            ...basics,
            forecast: forecast || DEFAULT_FORECAST,
            location: effectiveLocation,
            selectedScreens: selectedScreensOverride,
        };

        const data = formatValuesSavePreview(dataBeforeFormat);

        let id = null;
        try {
            const response = getData(
                await axios.post(endpoints.saveCampaignPreviewV2, data)
            );

            if (response.campaign) {
                id = response.campaign.id != null ? response.campaign.id : null;
                const statusCampaign = campaignStatus(response.campaign.status);

                if (id && id !== newCampaign.id) {
                    dispatch(newCampaignActions.setId(id));
                }

                dispatch(newCampaignActions.setStatus(statusCampaign));

                updateCampaignInListOfCampaigns(response.campaign);

                if (statusCampaign !== 'DRAFT') {
                    updateBudgets();
                }
                return id ?? newCampaign.id ?? null;
            } else {
                console.warn(
                    '[saveOrUpdate] ⚠️ The backend did not return a campaign in the response: ',
                    response
                );
            }
        } catch (error) {
            console.error('[saveOrUpdate] ❌ Error saving campaign:', error);
            throw new Error(error);
        }

        return id ?? newCampaign.id ?? null;
    };

    const updateCampaignInListOfCampaigns = (campaign) => {
        campaign.screen = campaign.screens ? campaign.screens : [];
        campaign.countries = campaign.countries_selected
            ? campaign.countries_selected
            : [];
        campaign.regions = campaign.regions_selected
            ? campaign.regions_selected
            : [];
        campaign.cities = campaign.cities_selected
            ? campaign.cities_selected
            : [];
        campaign.neighborhood = campaign.neighborhoods_selected
            ? campaign.neighborhoods_selected
            : [];
        campaign.pixel_tracker_script = campaign.pixel_tracker_script
            ? campaign.pixel_tracker_script
            : '';
        formatCampaign(campaign);
        let updated = false;
        const newCampaignList = JSON.parse(
            JSON.stringify(campaignList.campaigns)
        );
        newCampaignList.forEach((data) => {
            if (!updated) {
                data.campaigns.forEach((c) => {
                    if (!updated) {
                        if (c.id === campaign.id) {
                            Object.keys(c).forEach((key) => {
                                c[key] = campaign[key];
                            });
                            c.timezone_id = campaign.timezone_id
                                ? campaign.timezone_id
                                : null;
                            updated = true;
                        }
                    }
                });
            }
        });

        if (!updated) {
            newCampaignList.forEach((data) => {
                if (data.status === 'DRAFT') {
                    data.campaigns = [campaign, ...data.campaigns];
                }
            });

            if (!updated) {
                newCampaignList.push({
                    status: 'DRAFT',
                    page: 1,
                    campaigns: [campaign],
                });
            }

            const newStatuses = Object.assign({}, campaignList.statuses);
            newStatuses['DRAFT'] = newStatuses['DRAFT']
                ? newStatuses['DRAFT'] + 1
                : 1;
            dispatch(campaignsActions.setStatuses(newStatuses));
        }

        dispatch(campaignsActions.setCampaigns(newCampaignList));
    };

    // Continue the status of last campaign incomplete
    const continueNewCampaign = async (campaign) => {
        const currenciesStorage = localStorage.getItem('currencies')
            ? JSON.parse(
                  LZString.decompress(localStorage.getItem('currencies'))
              )
            : JSON.parse(localStorage.getItem('currencies'));
        if (campaign) {
            // !!the campaign is configured from editCampaign and saved to local storage for use here
            dispatch(newCampaignActions.setMany(campaign));
            if (campaign.selectedScreens.length) {
                dispatch(
                    screensActions.setSelectedScreensLocal(
                        campaign.selectedScreens
                    )
                );
            }
            if (currenciesStorage)
                dispatch(walletActions.setCurrencies(currenciesStorage));
            else if (currencies.length > 0)
                dispatch(walletActions.setCurrencies(currencies));
        }
        if (screens) {
            dispatch(screensActions.setScreens(screens));
        }
        return campaign ?? null;
    };

    const launchCampaign = async (basics, location, screens) => {
        try {
            const basicOverrides = basics ?? {};
            const baseTiming = {
                ...newCampaign.timing,
                ...basicOverrides.timing,
            };
            const campaignToSave = {
                ...newCampaign,
                ...basicOverrides,
                selectedScreens:
                    screens?.selectedScreens ?? newCampaign.selectedScreens,
                timing: {
                    ...baseTiming,
                    hoursEnabled: resolveHoursEnabled(baseTiming),
                },
            };

            const savedId = await saveOrUpdateCampaignPreview(
                basics,
                location,
                screens,
                campaignToSave
            );

            const campaignId = savedId ?? newCampaign.id;

            if (!campaignId) {
                console.error(
                    '[launchCampaign] ❌ campaignId is null/undefined. PUT operation will not be executed.'
                );
                return false;
            }

            const response = await axios.put(
                endpoints.finishCampaignV2 + campaignId
            );

            if (!response?.data?.error) {
                const statusesToRefresh = new Set(['ACTIVE']);
                const currentCampaignStatus =
                    campaignList.campaignsShowing.status;
                const campaignStatusBeforeLaunch = campaignToSave.status;

                if (currentCampaignStatus) {
                    statusesToRefresh.add(currentCampaignStatus);
                }

                if (campaignStatusBeforeLaunch) {
                    statusesToRefresh.add(campaignStatusBeforeLaunch);
                }

                await Promise.all([
                    getCampaignsPerStatus(),
                    ...Array.from(statusesToRefresh).map((status) =>
                        refreshCampaignListByStatus(status, 1)
                    ),
                ]);
                await updateBudgets();
                await syncForecastForCampaign({
                    ...campaignToSave,
                    id: campaignId,
                });

                dispatch(
                    notificationsActions.setNotification({
                        type: 'success',
                        title: t(
                            'sections.campaigns.new-campaign.wizard-launch.launch.campaign-launched-title'
                        ),
                        message: t(
                            'sections.campaigns.new-campaign.wizard-launch.launch.campaign-launched-message'
                        ),
                    })
                );

                history.push('/');
                return true;
            } else {
                console.warn(
                    '[launchCampaign] ⚠️ Backend has responded with an error:',
                    response?.data?.message
                );
                dispatch(
                    notificationsActions.setNotification({
                        type: 'warning',
                        title: t(
                            'sections.campaigns.new-campaign.wizard-launch.launch.campaign-not-launched-message'
                        ),
                        message: `${t(
                            'sections.campaigns.new-campaign.wizard-launch.launch.campaign-not-launched-message'
                        )}, ${response.data.message}`,
                    })
                );

                return false;
            }
        } catch (e) {
            console.error('[launchCampaign] Exception captured: ', e);
            ErrorMessage(e);
            return false;
        }
    };

    const updateBudgets = async () => {
        try {
            const budgetss = getData(await axios.get(endpoints.getBudgetsV2));
            const budgetStore = wallet.budgets;
            Object.entries(budgetStore).forEach((currency) => {
                if (budgetStore[currency[0]] != currency[1])
                    dispatch(walletActions.setBudgets(budgetss.budget));
            });
        } catch (error) {
            ErrorMessage(error);
        }
    };

    const getMargins = async () => {
        try {
            if (margins === -1) {
                const userId = { user_id: session.user.id };
                const response = await axios.post(endpoints.getMargins, userId);
                const rawData = response?.data ?? {};
                const fetchMargin = rawData?.data?.margins ?? rawData?.margins;
                setMargins(parseFloat(fetchMargin));
                return safeMargin;
            }
            return margins;
        } catch (e) {
            console.error({ e });
            dispatch(
                notificationsActions.setNotification({
                    type: 'warning',
                    title: t('common.notifications.error.title'),
                    message: t('common.notifications.error.errorData'),
                })
            );
            return 0;
        }
    };

    const getCreativeType = (extension) => {
        if (extension === '')
            return { isImage: false, isVideo: false, isScript: true };
        const imageExtensions = ['gif', 'jpg', 'png', 'jpeg'];
        const videoExtensions = ['mp4', 'webm'];
        const isImage = imageExtensions.some(
            (imageExtension) => imageExtension === extension
        );
        const isVideo = videoExtensions.some(
            (videoExtension) => videoExtension === extension
        );

        return { isImage, isVideo, isScript: !isImage && !isVideo };
    };

    const getResolution = (file, extension) => {
        const url = URL.createObjectURL(file);
        const { isImage, isVideo } = getCreativeType(extension);

        if (isImage) {
            return new Promise((resolve) => {
                const image = new Image();
                image.onload = function () {
                    resolve(this.width + 'x' + this.height);
                };
                image.src = url;
            });
        } else if (isVideo) {
            return new Promise((resolve) => {
                const videoElem = document.createElement('video');
                videoElem.src = url;
                videoElem.addEventListener('loadedmetadata', function () {
                    resolve(this.videoWidth + 'x' + this.videoHeight);
                });
            });
        } else {
            return 'no resolution';
        }
    };

    /**
     * Validate one zip file, one zip file is valid if contains one index.html inside. The zip can contains another files like
     * html, htm, css, webm, mp4, js, gif, jpg, png, svg, scss, less, php.
     * @param {File} zip
     * @return {Object} with 2 keys
     * - isValid: true if is valid
     * - error: in case that isValid=false, message contains the error message
     */
    const validateZip = async (zipFile) => {
        const content = await JSZip.loadAsync(zipFile);

        if (content.files['index.html']) {
            return { isValid: true, error: '' };
        } else {
            return {
                isValid: false,
                error: t(
                    'sections.campaigns.new-campaign.wizard-creatives.creatives.adType.errors.invalid-zip'
                ),
            };
        }
    };

    const validateFiles = async (files) => {
        const validExtensions = [
            'gif',
            'jpeg',
            'jpg',
            'png',
            'mp4',
            'webm',
            'zip',
        ];
        const isVideo = (extension) => ['mp4', 'webm'].includes(extension);
        const hasResolutionInFileName = (fileName) => {
            const fileNameWithoutExtension = fileName.replace(/\.[^/.]+$/, '');
            const resolution = getResolutionByString(fileNameWithoutExtension);
            if (resolution[0] !== 0 && resolution[1] !== 0) {
                const width = parseInt(resolution[0]);
                const height = parseInt(resolution[1]);
                return !isNaN(width) && !isNaN(height)
                    ? [width, height]
                    : false;
            } else {
                return false;
            }
        };

        let validFiles = [];
        const invalidFiles = [];

        await Promise.all(
            files.map(async (file) => {
                const extension = file.name.split('.').pop();
                const validExtension = validExtensions.some(
                    (e) => e === extension
                );
                if (validExtension) {
                    let resolution = await getResolution(file, extension);
                    if (
                        isVideo(extension) &&
                        hasResolutionInFileName(file.name)
                    ) {
                        resolution = hasResolutionInFileName(file.name);
                        resolution = `${resolution[0]}x${resolution[1]}`;
                    }

                    if (resolution !== 'no resolution') {
                        let isValidResolution = isVideo(extension)
                            ? true
                            : newCampaign.selectedScreens.some(
                                  (screen) => screen.size === resolution
                              );

                        if (isValidResolution) {
                            validFiles.push(file);
                        } else {
                            const error = t(
                                'sections.campaigns.new-campaign.wizard-creatives.creatives.adType.errors.invalid-resolution'
                            );
                            invalidFiles.push({ file, error });
                        }
                    } else {
                        // zip file
                        const { isValid, error } = await validateZip(file);
                        if (isValid) validFiles.push(file);
                        else invalidFiles.push({ file, error });
                    }
                } else {
                    const error = t(
                        'sections.campaigns.new-campaign.wizard-creatives.creatives.adType.errors.invalid-extension'
                    );
                    invalidFiles.push({ file, error });
                }
            })
        );

        return { validFiles, invalidFiles };
    };

    const uploadTester = async (file) => {
        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await axios.post(endpoints.uploadTester, formData);
            const data = response.data;
            return data;
        } catch (e) {
            return { error: true, message: e };
        }
    };

    // const uploadFiles = async (files, campaignId, campaignName) => {
    //     // const { validFiles, invalidFiles } = await validateFiles(files);
    //     const formData = new FormData();
    //     const showError = () => {
    //         dispatch(
    //             notificationsActions.setNotification({
    //                 type: 'error',
    //                 title: t(
    //                     'sections.campaigns.new-campaign.wizard-creatives.creatives.adType.errors.error-uploading-files'
    //                 ),
    //                 message: t(
    //                     'sections.campaigns.new-campaign.wizard-creatives.creatives.adType.errors.error-uploading-files-description'
    //                 ),
    //             })
    //         );
    //     };

    //     // if (validFiles.length === 0) {
    //     //     showError();
    //     //     return invalidFiles;
    //     // }

    //     //antes era validatedFiles.forEach
    //     files.forEach((file, index) => {
    //         formData.append('files[' + index + ']', file);
    //     });

    //     formData.append('campaign_preview_id', campaignId);
    //     formData.append('basename', campaignName);
    //     try {
    //         const response = getData(
    //             await axios.post(endpoints.uploadFiles, formData)
    //         );
    //         return response;
    //         if (response?.length === 0 || response.fail?.length > 0) {
    //             dispatch(
    //                 notificationsActions.setNotification({
    //                     type: 'warning',
    //                     title: t('common.notifications.error.title'),
    //                     message: t(
    //                         'common.notifications.error.errorExtensionaData'
    //                     ),
    //                 })
    //             );
    //             return invalidFiles;
    //         }
    //         const uploadedCreatives = response.success.map((creative) => ({
    //             ...creative.ad,
    //             extension: creative.extension,
    //         }));
    //         // dispatch(
    //         //     newCampaignActions.setCreatives([
    //         //         ...creatives,
    //         //         ...uploadedCreatives,
    //         //     ])
    //         // );
    //         return uploadedCreatives;
    //         if (invalidFiles.length > 0) {
    //             showError();
    //             return invalidFiles;
    //         }
    //     } catch (e) {
    //         console.error({ e });
    //         dispatch(
    //             notificationsActions.setNotification({
    //                 type: 'warning',
    //                 title: t('common.notifications.error.title'),
    //                 message: t('common.notifications.error.errorData'),
    //             })
    //         );
    //         // return invalidFiles;
    //     }
    // };

    const uploadFiles = async ({
        files,
        duration,
        campaignId,
        campaignName,
        notifyOnError = false,
    }) => {
        if (
            campaignId === null ||
            campaignId === undefined ||
            campaignId === ''
        ) {
            throw new Error('Missing campaign preview id');
        }

        const formData = new FormData();
        files.forEach((file, index) => {
            formData.append(`files[${index}]`, file);
        });

        formData.append('campaign_preview_id', campaignId);
        formData.append('basename', campaignName);
        formData.append('duration', Math.round(Number(duration) || 0));

        try {
            const axiosResp = await axios.post(endpoints.uploadFiles, formData);
            const data = getData(axiosResp);

            if (data?.error) throw new Error(data?.message || 'Upload error');

            const success = data?.success ?? [];
            if (!success.length) throw new Error('Upload returned no files');

            return success.map((item) => ({
                creativeId: String(item.ad.id),
                remoteUrl: item.thumbnail_url || item.url,
                raw: item,
            }));
        } catch (e) {
            console.error(e);
            if (notifyOnError) {
                dispatch(
                    notificationsActions.setNotification({
                        type: 'warning',
                        title: t('common.notifications.error.title'),
                        message: t('common.notifications.error.errorData'),
                    })
                );
            }
            throw e;
        }
    };

    const uploadCreativeFile = async ({
        file,
        duration,
        campaignId,
        campaignName,
    }) => {
        const uploaded = await uploadFiles({
            files: [file],
            duration,
            campaignId,
            campaignName,
            notifyOnError: false,
        });

        return uploaded[0];
    };

    // Creates dynamic HTML creatives from already resolved URLs (no file upload).
    const createUrlCreatives = async ({
        ads,
        duration,
        campaignId,
        campaignName,
    }) => {
        if (
            campaignId === null ||
            campaignId === undefined ||
            campaignId === ''
        ) {
            throw new Error('Missing campaign preview id');
        }

        const axiosResp = await axios.post(endpoints.uploadUrlCreatives, {
            campaign_preview_id: campaignId,
            basename: campaignName,
            duration: Math.round(Number(duration) || 0),
            ads,
        });
        const data = getData(axiosResp);

        if (data?.error) throw new Error(data?.message || 'Upload error');

        const success = data?.success ?? [];
        if (success.length !== ads.length) {
            throw new Error('Upload returned an unexpected number of ads');
        }

        return success.map((item) => ({
            creativeId: String(item.ad.id),
            remoteUrl: item.url,
            raw: item,
        }));
    };

    const linkMultipleCreativesToScreens = async (request) => {
        try {
            const response = getData(
                await axios.put(
                    endpoints.linkMultipleCreativesToScreens,
                    request
                )
            );

            if (response?.error) {
                throw new Error(
                    response?.message || 'Error linking creatives to screens'
                );
            }

            return true;
        } catch (e) {
            console.error({ e });
            dispatch(
                notificationsActions.setNotification({
                    type: 'warning',
                    title: t('common.notifications.error.title'),
                    message: t('common.notifications.error.errorData'),
                })
            );
            return false;
        }
    };

    const changeResolution = async (creative, resolution) => {
        const [width, height] = getResolutionByString(resolution);
        try {
            const request = { width, height };
            const response = getData(
                await axios.put(endpoints.assignSize + creative.id, request)
            );
            const creativeEdited = response.ad;
            if ('extension' in creative) {
                creativeEdited.extension = creative.extension;
            }
            const newCreatives = [];
            creatives.forEach((creative) => {
                if (creative.id !== creativeEdited.id)
                    newCreatives.push(creative);
                else newCreatives.push(creativeEdited);
            });
            dispatch(newCampaignActions.setCreatives(newCreatives));
            return response.ad;
        } catch (e) {
            console.error({ e });
            dispatch(
                notificationsActions.setNotification({
                    type: 'warning',
                    title: t('common.notifications.error.title'),
                    message: t('common.notifications.error.errorData'),
                })
            );
        }
    };

    const uploadScript = async (width, height, script) => {
        try {
            const request = {
                script,
                width,
                height,
                basename: newCampaign.baseName,
                campaign_preview_id: newCampaign.id,
            };

            let response = await axios.post(endpoints.uploadFiles, request);

            if (!response.data.error) {
                response = getData(response);
                dispatch(
                    newCampaignActions.setCreatives([...creatives, response.ad])
                );
                return response.ad;
            } else {
                dispatch(
                    notificationsActions.setNotification({
                        type: 'warning',
                        title: t(
                            'sections.campaigns.new-campaign.wizard-creatives.creatives.title'
                        ),
                        message: t(
                            'sections.campaigns.new-campaign.wizard-creatives.creatives.adType.errors.error-uploading-files-description'
                        ),
                    })
                );
            }
        } catch (e) {
            console.error({ e });
            dispatch(
                notificationsActions.setNotification({
                    type: 'warning',
                    title: t('common.notifications.error.title'),
                    message: t('common.notifications.error.errorData'),
                })
            );
        }
    };

    const vastInDisplay = async (width, height, script) => {
        try {
            const request = {
                script,
                vastInDisplay: script,
                width,
                height,
                basename: newCampaign.baseName,
                campaign_preview_id: newCampaign.id,
            };
            let response = await axios.post(endpoints.uploadFiles, request);

            if (!response.data.error) {
                response = getData(response);
                const responseCopy = Object.assign({}, response.ad);
                const newResponse = {
                    campaign_preview_id: responseCopy.campaign_preview_id,
                    created_at: responseCopy.created_at,
                    height: responseCopy.height,
                    id: responseCopy.id,
                    image: responseCopy.image,
                    is_secure: responseCopy.is_secure,
                    name: responseCopy.name,
                    original_filename: responseCopy.original_filename,
                    thumbnail_url: responseCopy.thumbnail_url,
                    title: responseCopy.title,
                    type: responseCopy.type,
                    updated_at: responseCopy.updated_at,
                    url: responseCopy.url,
                    user_id: responseCopy.user_id,
                    width: responseCopy.width,
                    extension: 'vast',
                };
                dispatch(
                    newCampaignActions.setCreatives([...creatives, newResponse])
                );
                return newResponse;
            } else {
                dispatch(
                    notificationsActions.setNotification({
                        type: 'warning',
                        title: t(
                            'sections.campaigns.new-campaign.wizard-creatives.creatives.title'
                        ),
                        message: t(
                            'sections.campaigns.new-campaign.wizard-creatives.creatives.adType.errors.error-uploading-files-description'
                        ),
                    })
                );
            }
        } catch (e) {
            console.error({ e });
            dispatch(
                notificationsActions.setNotification({
                    type: 'warning',
                    title: t('common.notifications.error.title'),
                    message: t('common.notifications.error.errorData'),
                })
            );
        }
    };

    const duplicateCreative = async (creative) => {
        try {
            const response = getData(
                await axios.post(endpoints.duplicateAd, { id: creative.id })
            );
            const duplicated = response.ad;
            if ('extension' in creative)
                duplicated.extension = creative.extension;
            dispatch(
                newCampaignActions.setCreatives([...creatives, duplicated])
            );
            return duplicated;
        } catch (e) {
            console.error({ e });
            dispatch(
                notificationsActions.setNotification({
                    type: 'warning',
                    title: t('common.notifications.error.title'),
                    message: t('common.notifications.error.errorData'),
                })
            );
        }
    };

    const removeCreative = async (creativeid) => {
        try {
            const response = await axios.delete(
                endpoints.removeAd + creativeid
            );
            if (response.error) {
                throw new Error('Error deleting creative');
            }
            return true;
        } catch (e) {
            console.error({ e });
            dispatch(
                notificationsActions.setNotification({
                    type: 'warning',
                    title: t('common.notifications.error.title'),
                    message: t('common.notifications.error.errorData'),
                })
            );
            return false;
        }
    };

    /**
     * Lets you delete multiple creatives in on query
     * @param Array creativesarr is an Array of creatives
     */
    const removeMultipleCreatives = async (creativesarr) => {
        try {
            let newCreatives = creatives;
            let promisesToDelete = [];
            for (let i = 0; i < creativesarr.length; i++) {
                promisesToDelete.push(
                    axios.delete(endpoints.removeAd + creativesarr[i].id)
                );
            }
            const responses = await Promise.allSettled(promisesToDelete);
            let withoutError = [];
            let withError = [];
            responses.forEach((response) => {
                const idDeleted = response;
                const idResponse =
                    idDeleted.value.request.responseURL.split('/ad/')[1];
                if (!response.value.data.error) {
                    withoutError.push({
                        ...response.value.data,
                        id: idResponse,
                    });
                    newCreatives = newCreatives.filter(
                        (c) => c.id != idResponse
                    );
                } else {
                    withError.push({ ...response.value.data, id: idResponse });
                    console.error({ ...response.value.data, id: idResponse });
                }
            });
            dispatch(newCampaignActions.setCreatives(newCreatives));
            return { withError, withoutError };
        } catch (e) {
            console.error({ e });
            dispatch(
                notificationsActions.setNotification({
                    type: 'warning',
                    title: t('common.notifications.error.title'),
                    message: t('common.notifications.error.errorData'),
                })
            );
        }
    };

    //add translations to retargetly interests
    const getRetargetlyMetadata = () => {
        const interests = [];
        retargetlyMetadata.data.segments.forEach((segment) => {
            if (segment.id === 'rtg_interests') {
                segment.keys.forEach((key) => {
                    const interest = {
                        text: t(
                            'sections.campaigns.new-campaign.wizard-audiences.providers.retargetly.' +
                                key.id
                        ),
                        id: key.id,
                    };
                    interests.push(interest);
                });
            }
        });
        const newRetargetlyData = JSON.parse(
            JSON.stringify(retargetlyMetadata)
        );
        newRetargetlyData.data.segments.push({
            keys: interests,
            name: 'interests',
            id: 'interests',
        });

        return newRetargetlyData;
    };

    const getProviderMetadata = (provider) => {
        let metadata;
        switch (provider) {
            case 'scopesi':
                metadata = scopesiMetadata;
                break;
            case 'retargetly':
                metadata = getRetargetlyMetadata();
                break;
            case 'telefonica':
                metadata = telefonicaMetadata;
                break;
            case 'cinnecta':
                metadata = cinnectaMetadata;
                break;
            case 'onemata':
                metadata = onemataMetadata;
                break;
            default:
                metadata = scopesiMetadata;
                break;
        }
        const selectedProvider = metadata.data;
        dispatch(newCampaignActions.setDemographics(selectedProvider));
    };

    const getWeekDays = () => {
        let weekDays = newCampaign.timing.daysEnabled;
        weekDays = formatDaysFromNumbers(weekDays);
        if (newCampaign.demographics.id === 'scopesi') {
            return weekDays;
        } else {
            const workingDays = ['Mon', 'Tue', 'Wed', 'Thi', 'Fri'];
            const weekendDays = ['Sun', 'Sat'];
            const weekDaysWeekend = [];
            if (
                weekDays.some((day) =>
                    workingDays.some((woorkingDay) => woorkingDay === day)
                )
            )
                weekDaysWeekend.push('working');
            if (
                weekDays.some((day) =>
                    weekendDays.some((weekendDay) => weekendDay === day)
                )
            )
                weekDaysWeekend.push('weekend');
            return weekDaysWeekend;
        }
    };

    const normalizeGenderSelection = (genderList = []) => {
        if (!Array.isArray(genderList) || genderList.length === 0) {
            return [];
        }
        const uniqueGenders = [
            ...new Set(genderList.filter((gender) => !!gender)),
        ];
        const hasMale = uniqueGenders.includes('male');
        const hasFemale = uniqueGenders.includes('female');
        if (hasMale && hasFemale && uniqueGenders.length >= 2) {
            return [];
        }
        return uniqueGenders;
    };

    const formatCampaignLikeJson = (campaign, margin) => {
        const valuesAccuracy = {
            0: 'low',
            1: 'mid',
            2: 'high',
        };
        const request = {
            campaign: 0,
            budget: 0,
            start_period: 0,
            end_period: 0,
            name: '',
            user: session.user.id,
            advertiser: '',
            brand: '',
            provider: [],
            hasAudienceFilter: false,
            audience: {
                incomeLevel: [],
                gender: [],
                age: [],
                accuracy: '',
            },
            tcpm: 0,
            cpm: 0,
            asap: 1,
            margin: 0,
            cpm_optimize: 0,
            creative_categories: [],
            tmzn: 0,
            currency: '',
            deals_ids: [],
            ads: [],
            time_hours: [],
            time_days: [],
            frequency_cap: {},
            screens_sizes: [],
            screens: [],
        };
        const normalizedGender = normalizeGenderSelection(
            campaign.segments.gender
        );
        let deals = [];
        let screenSizes = [];
        let ads = [];
        campaign.selectedScreens.map((screen) => {
            const dealsKeys = Object.keys(screen.deals);
            if (dealsKeys.length > 0) {
                dealsKeys.map((key) => {
                    if (screen.deals[key] && screen.deals[key]['SPOT']) {
                        deals.push(screen.deals[key]['SPOT'].rules[0].deal_id);
                    }

                    if (screen.deals[key] && screen.deals[key]['CPM']) {
                        deals.push(screen.deals[key]['CPM'].rules[0].deal_id);
                    }
                });
            }
            screenSizes.push(`${screen.width}x${screen.height}`);
            if (
                ads.findIndex(
                    (ad) =>
                        ad.width === screen.width && ad.height === screen.height
                ) === -1
            ) {
                ads.push({
                    id: 1,
                    type: 1,
                    url: '',
                    click_url: '',
                    media_url: '',
                    last_update: '',
                    width: screen.width,
                    height: screen.height,
                    url_secure: '',
                });
            }
        });
        request.margin = margin;
        request.campaign = campaign.id;
        request.budget = campaign.budget.budget;
        request.start_period = campaign.timing.periodValue[0];
        request.end_period = campaign.timing.periodValue[1];
        request.name = campaign.name;
        request.brand = campaign.brand;
        request.provider = [campaign.demographics.id];
        request.hasAudienceFilter =
            campaign.segments.incomeLevel.length > 0 ||
            normalizedGender.length > 0 ||
            campaign.segments.age.value.length > 0;
        request.audience = {
            incomeLevel: campaign.segments.incomeLevel,
            gender: normalizedGender,
            age: campaign.segments.age.value,
            accuracy: valuesAccuracy[campaign.segments.accuracy.sliderValue],
        };
        // the cpm max to use the bidder
        request.tcpm = campaign.budget.cpm;
        // the max cpm without margin
        request.cpm = campaign.budget.cpm * 1000;
        // TODO: the asap and pacing are the same
        const pacingStrategy = normalizeStrategyValue(
            campaign.timing?.strategy
        );
        const isAsapStrategy = pacingStrategy === 'asap';
        request.asap = isAsapStrategy ? 1 : 0;
        request.frequency_cap = {
            pacing: isAsapStrategy ? 1 : 0,
        };
        request.creative_categories = campaign.selectedCreativeCategories;
        request.tmzn = campaign.timing.selectedTimezone.gmt;
        request.currency = campaign.selectedCurrency.short_name;
        request.deals_ids = deals;
        request.ads = ads;
        request.time_hours = resolveHoursEnabled(campaign.timing);
        request.time_days = request['days_enabled'].length
            ? request['days_enabled']
            : [];
        request.screens = campaign.selectedScreens.map((screen) => ({
            ...screen,
            id: screen.external_id,
        }));
        request.screens_sizes = [...new Set(screenSizes)];
        if (campaign.dailyCap.isActive) {
            if (campaign.dailyCap.isDistribution) {
                request.daily_limit = 0;
                request.daily_limit_unit = 0;
            } else {
                request.daily_limit = campaign.dailyCap.isBudget
                    ? campaign.dailyCap.budget
                    : campaign.dailyCap.impressions;
                // 1 limit by budget, 0 by impressions
                request.daily_limit_unit = campaign.dailyCap.isBudget ? 1 : 0;
            }
        }
        return request;
    };

    const normalizeToInt = (value, options = {}) => {
        const { extractDigits = false, defaultValue = null } = options;

        if (value === null || value === undefined) {
            return defaultValue;
        }

        if (typeof value === 'number' && Number.isFinite(value)) {
            return Math.trunc(value);
        }

        let candidate = value;

        if (typeof candidate === 'string') {
            let normalized = candidate.trim();
            if (extractDigits) {
                const digitsMatch = normalized.match(/\d+/);
                normalized = digitsMatch ? digitsMatch[0] : '';
            }
            const parsed = parseInt(normalized, 10);
            return Number.isNaN(parsed) ? defaultValue : parsed;
        }

        if (typeof candidate === 'object' && extractDigits) {
            const serialized = JSON.stringify(candidate);
            const digitsMatch = serialized.match(/\d+/);
            if (digitsMatch) {
                const parsed = parseInt(digitsMatch[0], 10);
                return Number.isNaN(parsed) ? defaultValue : parsed;
            }
            return defaultValue;
        }

        const parsed = parseInt(candidate, 10);
        return Number.isNaN(parsed) ? defaultValue : parsed;
    };

    const normalizeScreens = (screens = []) =>
        Array.isArray(screens)
            ? screens
                  .map((s) =>
                      typeof s === 'string'
                          ? s
                          : typeof s === 'object'
                            ? (s.internal_id ?? null)
                            : null
                  )
                  .filter(Boolean)
            : [];

    const ensureIsoDateString = (value) => {
        if (value == null) return null;
        const isoDate = dateToIso8601(value);
        return isoDate === -1 ? null : isoDate;
    };

    const resolveCampaignPeriodRange = (campaign) => {
        const timing = campaign?.timing;
        const pv = timing?.periodValue;

        return [
            campaign?.start_period ?? timing?.start_period ?? pv?.[0] ?? null,
            campaign?.end_period ?? timing?.end_period ?? pv?.[1] ?? null,
        ];
    };

    // Returns { 'YYYY-MM-DD': number } with days inside the campaign period,
    // or null when daily caps are inactive/empty. Absent day = no cap.
    const buildDailyCapsPayload = (dailyCaps, startPeriod, endPeriod) => {
        if (!dailyCaps?.isActive) return null;

        const caps = dailyCaps.caps ?? {};
        const startIso = ensureIsoDateString(startPeriod);
        const endIso = ensureIsoDateString(endPeriod);

        const filtered = {};
        Object.entries(caps).forEach(([day, value]) => {
            const amount = Number(value);
            if (!Number.isFinite(amount) || amount < 0) return;
            if (startIso && day < startIso) return;
            if (endIso && day > endIso) return;
            filtered[day] = amount;
        });

        return Object.keys(filtered).length > 0 ? filtered : null;
    };

    const resolveCampaignTimezoneOffset = (campaign) => {
        const timing = campaign?.timing;
        const gmt = timing?.selectedTimezone?.gmt;

        if (gmt != null) {
            const parsed = Number(gmt);
            if (!Number.isNaN(parsed)) return parsed;
        }

        if (typeof campaign?.tmzn === 'number') return campaign.tmzn;

        if (timing?.timezones?.length > 0) {
            const guessed = guessTimezoneOfBrowser(timing.timezones);
            if (guessed?.gmt != null) return guessed.gmt;
        }

        return -new Date().getTimezoneOffset() / 60;
    };

    const ensureForecastCampaignId = (value) => {
        const fallback = String(Date.now());

        if (value != null && typeof value === 'object') {
            return 'id' in value
                ? ensureForecastCampaignId(value.id)
                : fallback;
        }

        const id =
            typeof value === 'number' && Number.isFinite(value)
                ? String(value)
                : String(value ?? '').trim();

        return id || fallback;
    };

    const normalizeBudgetValue = (value) => {
        if (value == null) return 0;

        if (typeof value === 'number') {
            return Number.isFinite(value) ? value : 0;
        }

        if (typeof value === 'string') {
            const cleaned = value.trim().replace(',', '.');
            const parsed = Number(cleaned);
            return Number.isFinite(parsed) ? parsed : 0;
        }

        return 0;
    };

    const formatCampaignForForecastV3 = (campaign, forecastOptions = {}) => {
        let pricingMode = 'spot';
        if (userCpmMode === 1 && campaign.budget.cpmOptimizer) {
            pricingMode = 'cpm';
        }
        const timing = campaign?.timing ?? {};
        const strategy = normalizeStrategyValue(timing?.strategy);
        const alpha = timing?.alpha ?? 1.5;

        const [startPeriodRaw, endPeriodRaw] =
            resolveCampaignPeriodRange(campaign);
        const startPeriod = ensureIsoDateString(startPeriodRaw);
        const endPeriod = ensureIsoDateString(endPeriodRaw);
        const user = normalizeToInt(campaign?.user ?? userId, {
            extractDigits: true,
        });
        const campaignId = ensureForecastCampaignId(campaign?.id);
        const timezoneOffset = resolveCampaignTimezoneOffset(campaign);

        const filters = {};
        const resolveSegmentValues = (primary, fallback = []) =>
            primary?.length ? primary : fallback?.length ? fallback : [];

        const selectedAgesRaw = resolveSegmentValues(
            campaign.segments?.age?.value,
            campaign.scopesi_ages
        );
        const selectedAges = selectedAgesRaw.filter((age) => age !== null);

        // This is because Forecast-report need plain ints to work with, not string array elements
        const parseAgeBoundForForecast = (value, pickUpper = false) => {
            if (typeof value === 'string') {
                const [startRaw, endRaw] = value.split('-');
                const start = parseInt(startRaw, 10);
                const end = parseInt(endRaw, 10);
                if (!Number.isNaN(start) && !Number.isNaN(end)) {
                    return pickUpper ? end : start;
                }
            } else if (typeof value === 'number') {
                return value;
            }
            return null;
        };

        let defaultAgeFrom = 0;
        let defaultAgeTo = 99;

        if (selectedAges.length > 0) {
            const lowerCandidate = parseAgeBoundForForecast(
                selectedAges[0],
                false
            );
            if (lowerCandidate !== null) {
                defaultAgeFrom = lowerCandidate;
            }
            const upperCandidate = parseAgeBoundForForecast(
                selectedAges[selectedAges.length - 1],
                true
            );
            if (upperCandidate !== null) {
                defaultAgeTo = upperCandidate;
            }
        }

        filters.age_from = defaultAgeFrom;
        filters.age_to = defaultAgeTo;
        const gender = normalizeGenderSelection(
            resolveSegmentValues(
                campaign.segments?.gender,
                campaign.scopesi_gender
            )
        );
        if (gender.length === 1) {
            filters.gender = gender[0];
        }

        const incomeLevels = resolveSegmentValues(
            campaign.segments?.incomeLevel,
            campaign.scopesi_nse
        );

        if (incomeLevels.length) {
            filters.income_levels = incomeLevels;
        }

        filters.general_demographic = Boolean(
            forecastOptions.generalDemographic ??
            campaign.generalDemographic ??
            false
        );

        const time_hours = resolveHoursEnabled(timing);
        const totalBudget = normalizeBudgetValue(campaign?.budget?.budget ?? 0);
        const totalSpent = normalizeBudgetValue(campaign?.totalSpent);
        const remainingBudget = Math.max(totalBudget - totalSpent, 0);
        const dailyCaps = buildDailyCapsPayload(
            campaign?.dailyCaps ?? newCampaign?.dailyCaps,
            startPeriodRaw,
            endPeriodRaw
        );

        return {
            campaign: {
                id: campaignId,
                name: campaign.name,
                user: user,
                total_budget: remainingBudget,
                currency: campaign.selectedCurrency?.short_name,
                campaign_tmz: timezoneOffset,
                start_period: startPeriod,
                end_period: endPeriod,
                time_hours: time_hours,
                screens: normalizeScreens(campaign.selectedScreens),
                pricing_mode: pricingMode,
                strategy: strategy,
                alpha: alpha,
                ...(dailyCaps ? { daily_caps: dailyCaps } : {}),
            },
            filters,
        };
    };

    const getForecastRequestKey = (campaign, forecastOptions = {}) => {
        return JSON.stringify(
            formatCampaignForForecastV3(campaign, forecastOptions)
        );
    };

    const formatForecastDetailsRequest = (campaignPayload) => {
        const safeStartPeriod = campaignPayload?.start_period;
        const safeEndPeriod = campaignPayload?.end_period;
        const user = normalizeToInt(campaignPayload.user, {
            extractDigits: true,
            defaultValue: 0,
        });
        const campaignId = ensureForecastCampaignId(campaignPayload?.id);

        return {
            id: campaignId,
            screens: normalizeScreens(campaignPayload.screens || []),
            pricing_mode: campaignPayload.pricing_mode,
            currency: campaignPayload.currency,
            total_budget: campaignPayload.total_budget,
            strategy: campaignPayload.strategy,
            alpha: campaignPayload.alpha,
            name: campaignPayload.name,
            user: user,
            campaign_tmz: campaignPayload.campaign_tmz,
            start_period: safeStartPeriod,
            end_period: safeEndPeriod,
            time_hours: campaignPayload.time_hours,
            ...(campaignPayload.daily_caps
                ? { daily_caps: campaignPayload.daily_caps }
                : {}),
        };
    };

    const formatForecastDetails = (detailsResponse) => {
        let detailsArray = [];
        let impressionsPerScreenArray = [];
        if (Array.isArray(detailsResponse)) {
            detailsArray = detailsResponse;
        } else if (detailsResponse && typeof detailsResponse === 'object') {
            detailsArray = detailsResponse?.data;
            impressionsPerScreenArray = detailsResponse?.impressions_per_screen;
        } else {
            return {
                schedules: [],
                impressionsPerScreen: [],
            };
        }

        const weekDays = [
            'Sunday',
            'Monday',
            'Tuesday',
            'Wednesday',
            'Thursday',
            'Friday',
            'Saturday',
        ];

        const toNumber = (value) => {
            if (value === null || value === undefined) {
                return 0;
            }
            const numericValue = Number(value);
            return Number.isNaN(numericValue) ? 0 : numericValue;
        };

        const screenNameLookup = new Map();
        const schedules = detailsArray.map((detail) => {
            const rawWeekday =
                typeof detail.weekday === 'number' ? detail.weekday : null;
            const normalizedWeekday =
                rawWeekday !== null ? ((rawWeekday % 7) + 7) % 7 : rawWeekday;
            const weekDayName =
                normalizedWeekday !== null && weekDays[normalizedWeekday]
                    ? weekDays[normalizedWeekday]
                    : detail.weekday;
            if (detail?.screen_id && detail?.name) {
                screenNameLookup.set(detail.screen_id, detail.name);
            }
            return {
                poiId: detail.screen_id,
                ScreenExternalId: detail.screen_external_id,
                screenName: detail.screen_name,
                screenType: detail.screen_type,
                date: detail.date,
                weekDay: weekDayName,
                weekdayNumber: normalizedWeekday,
                hour: detail.hour,
                impressions: toNumber(detail.impressions),
                totalImpacts: toNumber(detail.impacts),
                targetUniverse: toNumber(detail.total_reach),
                totalCoverageAbs: toNumber(detail.total_reach),
                totalCoverageRel: detail.total_coverage_rel ?? null,
                costs: toNumber(detail.costs),
                cpm: toNumber(detail.cpm),
                cpi: toNumber(detail.cpi),
                totalFrequency: toNumber(detail.frecuency),
                grp: toNumber(detail.grp),
                cps: toNumber(detail.cps),
                spm: toNumber(detail.spm),
            };
        });

        const impressionsPerScreen = impressionsPerScreenArray
            .map((entry) => {
                if (!entry) return null;

                const screenId = entry?.screen_id;
                const averageImpressions = toNumber(
                    entry?.average_impressions ?? entry?.averageImpressions
                );
                const expositionTime = toNumber(
                    entry?.exposition_time ?? entry?.expositionTime
                );

                return {
                    screenId,
                    impressions: toNumber(entry.impressions),
                    averageImpressions,
                    expositionTime,
                };
            })
            .filter(Boolean);

        return {
            schedules,
            impressionsPerScreen,
        };
    };

    const calculateForecast = async (campaign, forecastOptions = {}) => {
        try {
            const providedScreens = Array.isArray(
                forecastOptions?.selectedScreens
            )
                ? forecastOptions.selectedScreens
                : null;
            const fallbackScreens = Array.isArray(campaign?.selectedScreens)
                ? campaign.selectedScreens
                : Array.isArray(newCampaign?.selectedScreens)
                  ? newCampaign.selectedScreens
                  : [];
            const normalizedScreens = providedScreens ?? fallbackScreens;

            const campaignSource = {
                ...(campaign ?? newCampaign),
                selectedScreens: normalizedScreens,
            };
            const campaignFormatted = formatCampaignForForecastV3(
                campaignSource,
                forecastOptions
            );
            const { data: summaryData } = await axios.post(
                endpoints.getForecasterV2,
                campaignFormatted
            );

            let detailsData = {
                schedules: [],
                impressionsPerScreen: [],
            };
            if (campaignFormatted.campaign.screens.length > 0) {
                try {
                    const campaignPayload = {
                        ...campaignFormatted.campaign,
                        strategy:
                            campaignFormatted.campaign.strategy || 'evenly',
                    };
                    const detailsRequest =
                        formatForecastDetailsRequest(campaignPayload);
                    const { data: detailsResponse } = await axios.post(
                        endpoints.getForecasterDetailsV2,
                        detailsRequest
                    );
                    detailsData = formatForecastDetails(detailsResponse);
                } catch (detailsError) {
                    console.error('forecast details error', detailsError);
                }
            }

            const mirrorGeneralDemographics = shouldMirrorGeneralDemographics(
                campaignFormatted?.filters
            );
            const formattedMetrics = formatMetrics(summaryData, {
                mirrorGeneralDemographics,
            });
            formattedMetrics.schedules = detailsData.schedules;
            formattedMetrics.impressionsPerScreen =
                detailsData.impressionsPerScreen;
            const distributionRows = Object.entries(
                formattedMetrics.screenTypeDistribution || {}
            ).map(([screenType, share]) => ({
                screenType,
                value: share,
            }));
            return {
                metrics: formattedMetrics,
                distributionRows,
                requestKey: JSON.stringify(campaignFormatted),
            };
        } catch (e) {
            console.error(e.message);
            dispatch(
                notificationsActions.setNotification({
                    type: 'warning',
                    title: t(
                        'common.notifications.error.errorCalculateForecast-title'
                    ),
                    message: t(
                        'common.notifications.error.errorCalculateForecast-description'
                    ),
                })
            );
        }
    };

    const syncForecastForCampaign = async (campaignPayload) => {
        try {
            const campaignSummary =
                formatCampaignForForecastV3(campaignPayload)?.campaign;
            if (!campaignSummary) return;

            await axios.post(endpoints.forecastCampaignV2, {
                ...campaignSummary,
                is_campaign_launching: true,
            });
        } catch (error) {
            console.error(
                '[syncForecastForCampaign] forecast request failed',
                error
            );
        }
    };

    const formatMetrics = (response, options = {}) => {
        const { mirrorGeneralDemographics = false } = options;
        const responseData =
            response &&
            typeof response === 'object' &&
            !Array.isArray(response) &&
            response.data &&
            typeof response.data === 'object'
                ? response.data
                : response;
        const payload =
            responseData &&
            typeof responseData === 'object' &&
            responseData.data &&
            typeof responseData.data === 'object'
                ? responseData.data
                : responseData && typeof responseData === 'object'
                  ? responseData
                  : {};
        const metrics = {};
        const currencySymbol =
            newCampaign.selectedCurrency?.symbol !== undefined
                ? newCampaign.selectedCurrency.symbol
                : '';
        const spentBudget = Number(payload?.spent_budget || 0);
        const totalBudget = Number(
            response?.total_budget ??
                responseData?.total_budget ??
                payload?.total_budget ??
                0
        );
        const dayDuration = payload?.day_duration || 0;
        const generalUniverse = Number(payload?.general_universe || 0);
        let targetUniverse = Number(payload?.target_universe || 0);
        const screensQuantity = payload?.screens_quantity || 0;
        const totalImpressions = Number(payload?.total_impressions || 0);
        const reproductionTime = payload?.reproduction_time || 0;
        const screenTypeDistribution = payload?.screen_type_distribution || {};
        const hoursWithNoPurchases = Number(
            payload?.hours_with_no_purchases || 0
        );

        const targetData = payload?.target || {};
        const generalData = payload?.general || {};
        if (mirrorGeneralDemographics) {
            targetUniverse = generalUniverse;
        }

        const toFrequency = (data) =>
            Number(
                data?.frecuency !== undefined
                    ? data.frecuency
                    : data?.frequency || 0
            );

        const generalCoverageRaw = Number(generalData?.coverage || 0);
        const generalCoveragePercent = generalCoverageRaw * 100;
        const generalCoverageNumericAmount =
            generalData?.coverage_numeric_amount || 0;
        const targetCoverageRaw = Number(targetData?.coverage || 0);
        const targetCoveragePercent = targetCoverageRaw * 100;
        const coveragePercent = mirrorGeneralDemographics
            ? generalCoveragePercent
            : targetCoveragePercent;
        const targetCoverageNumericAmount = mirrorGeneralDemographics
            ? generalCoverageNumericAmount
            : targetData?.coverage_numeric_amount || 0;
        const targetTotalImpacts = Number(targetData?.total_impacts || 0);
        const targetGrp = Number(targetData?.grp || 0);
        const targetFrequency = toFrequency(targetData);
        const cpmTarget = Number(targetData?.cpm || 0);
        const generalFrequency = toFrequency(generalData);

        metrics.schedules = [];
        metrics.spentBudget = spentBudget;
        metrics.totalBudget = totalBudget;
        metrics.total_budget = totalBudget;
        metrics.costs = spentBudget;
        metrics.dayDuration = dayDuration;
        metrics.duration = dayDuration;
        metrics.generalUniverseRaw = generalUniverse;
        metrics.general_universe = generalUniverse;
        metrics.generalUniverse = generalUniverse;
        metrics.targetUniverseRaw = targetUniverse;
        metrics.target_universe = targetUniverse;
        metrics.targetUniverse = targetUniverse;
        metrics.screensQuantity = screensQuantity;
        metrics.screens = screensQuantity;
        metrics.totalImpressions = totalImpressions;
        metrics.reproductionTime = reproductionTime;
        metrics.screenTypeDistribution = screenTypeDistribution;
        metrics.hoursWithNoPurchases = hoursWithNoPurchases;
        metrics.generalMetrics = {
            coverage: generalCoveragePercent,
            coverageNumericAmount: generalCoverageNumericAmount,
            totalImpacts: Number(generalData?.total_impacts || 0),
            cpm: Number(generalData?.cpm || 0),
            grp: Number(generalData?.grp || 0),
            frequency: generalFrequency,
        };
        metrics.targetMetrics = {
            coverage: coveragePercent,
            coverageNumericAmount: targetCoverageNumericAmount,
            totalImpacts: targetTotalImpacts,
            cpm: cpmTarget,
            grp: targetGrp,
            frequency: targetFrequency,
        };

        metrics.frequency = isNaN(targetFrequency)
            ? '-'
            : lastTwoDecimal(targetFrequency);
        metrics.grp = isNaN(targetGrp) ? '-' : lastTwoDecimal(targetGrp);
        metrics.cpm = `${currencySymbol}${formatNumber(cpmTarget, 'currency')}`;
        const coverageAbs =
            targetUniverse > 0 ? targetUniverse * coveragePercent : 0;
        metrics.coverageAbs = formatNumber(coverageAbs, 'short');
        metrics.coverage = `${formatNumber(coveragePercent, 'cpi')}%`;

        metrics.targetImpacts = targetTotalImpacts;
        metrics.totalImpacts = formatNumber(targetTotalImpacts, 'currency');

        const cpiRaw =
            targetTotalImpacts > 0 ? spentBudget / targetTotalImpacts : 0;

        metrics.cpi = `${currencySymbol}${formatNumber(
            cpiRaw,
            'currency',
            null,
            {
                decimals: cpiRaw < 0.01 ? 4 : 2,
            }
        )}`;

        const impressionsRounded = Math.round(totalImpressions);
        metrics.adImpressions = impressionsRounded;
        metrics.impressions = totalImpressions;

        metrics.notMatchingScreens =
            payload?.not_matching_screens || payload?.notMatchingScreens || [];
        metrics.screensNotUsed =
            payload?.screens_not_used || payload?.screensNotUsed || [];
        metrics.screensWithoutProvider =
            payload?.screens_without_provider ||
            payload?.screensWithoutProvider ||
            [];
        metrics.validScreens =
            payload?.valid_screens || payload?.validScreens || [];
        metrics.screensUsed =
            payload?.screens_used || payload?.screensUsed || [];
        metrics.screenCountWarnings =
            payload?.screen_count_warnings ||
            payload?.screenCountWarnings ||
            [];
        metrics.isUnderspend =
            payload?.is_underspend !== undefined
                ? payload.is_underspend
                : payload?.isUnderspend || false;
        return metrics;
    };

    const activateCampaign = async (campaign) => {
        try {
            await axios.put(endpoints.activateCampaignV2 + campaign.id);

            moveFrom('INACTIVE', 'ACTIVE', campaign);

            dispatch(
                notificationsActions.setNotification({
                    type: 'success',
                    title: t(
                        'common.notifications.success.campaignSuccessfullActivated-title'
                    ),
                    message: t(
                        'common.notifications.success.campaignSuccessfullActivated-description'
                    ),
                })
            );
        } catch (error) {
            ErrorMessage(error);
        }
    };

    const deactivateCampaign = async (campaign) => {
        try {
            await axios.put(endpoints.deactivateCampaignV2 + campaign.id);

            moveFrom('ACTIVE', 'INACTIVE', campaign);

            dispatch(
                notificationsActions.setNotification({
                    type: 'success',
                    title: t(
                        'common.notifications.success.campaignSuccessfullDeactivated-title'
                    ),
                    message: t(
                        'common.notifications.success.campaignSuccessfullDeactivated-description'
                    ),
                })
            );
            return true;
        } catch (error) {
            ErrorMessage(error);
            return false;
        }
    };

    const restoreCampaign = async (campaign) => {
        try {
            const { data } = await axios.put(
                endpoints.restoreCampaignV2 + campaign.id
            );
            if (!data.error) {
                moveFrom('DELETED', 'INACTIVE', campaign);
            }

            dispatch(
                notificationsActions.setNotification({
                    type: 'success',
                    title: t(
                        'common.notifications.success.campaignSuccesfullRestore-title'
                    ),
                    message: t(
                        'common.notifications.success.campaignSuccesfullRestore-description'
                    ),
                })
            );
        } catch (error) {
            ErrorMessage(error);
        }
    };

    const deleteCampaign = async (campaign, status) => {
        try {
            const { data } = await axios.delete(
                endpoints.removeCampaignV2 + campaign.id
            );

            await updateBudgets();
            // validation so that the deleted campaign is removed to the current section if it
            // does not have that current status (for example active)
            // JOB_PENDING for when one of this list is deleted,
            // since the status is not inactive, then it can be deleted
            const isRemoveoCurrentSection = status == 'JOB_PENDING';
            const newStatuses = JSON.parse(
                JSON.stringify(campaignList.statuses)
            );
            newStatuses[campaign.status] = newStatuses[campaign.status] - 1;
            if (newStatuses[campaign.status] === 0)
                delete newStatuses[campaign.status];
            let newCampaignsData = Array.from(campaignList.campaigns);
            newCampaignsData = newCampaignsData.map((data) => {
                if (
                    data.status === campaign.status ||
                    isRemoveoCurrentSection
                ) {
                    const newData = Object.assign({}, data);
                    newData.campaigns = newData.campaigns.filter(
                        (c) => c.id !== campaign.id
                    );
                    return newData;
                } else {
                    return data;
                }
            });

            getCampaignsPerStatus();
            dispatch(campaignsActions.setCampaigns(newCampaignsData));
        } catch (error) {
            ErrorMessage(error);
        }
    };

    const duplicateCampaign = async (campaign, status) => {
        try {
            const response = getData(
                await axios.post(endpoints.duplicateCampaignV2 + campaign.id)
            );
            const duplicatedCampaign = response.campaign;
            duplicatedCampaign.screen = campaign.screen;
            formatCampaign(duplicatedCampaign);
            let newCampaignsData = campaignList.campaigns.map((data) => {
                // add campaign cloned to postiion 0 in list
                // if it's already saved in previus querys
                if (data.status === duplicatedCampaign.status) {
                    let copyArray = [...data.campaigns];
                    copyArray.unshift(duplicatedCampaign);
                    let copyData = { ...data, campaigns: copyArray };
                    return copyData;
                } else {
                    return data;
                }
            });

            const newCurrent = newCampaignsData.find(
                (data) => data.status === campaignList.campaignsShowing.status
            );

            await getCampaignsPerStatus();
            dispatch(campaignsActions.setCampaigns(newCampaignsData));
            dispatch(campaignsActions.changeCampaigns(newCurrent));
        } catch (error) {
            ErrorMessage(error);
        }
    };

    /**
     * Move one campaign from one list to another list
     * @param {String} status1 status of the list where is the campaign that have to move to another list
     * @param {String} status2 status of the target list to move the campaign
     * @param {*} campaign with id of the campaign to move
     * @returns List with states of each status
     */
    const moveFrom = (status1, status2, campaign) => {
        if (!campaignList.statuses || !Array.isArray(campaignList.campaigns)) {
            return;
        }

        const newStatuses = JSON.parse(JSON.stringify(campaignList.statuses));
        newStatuses[status1] = newStatuses[status1] - 1;
        if (newStatuses[status1] === 0) delete newStatuses[status1];
        newStatuses[status2] = newStatuses[status2]
            ? newStatuses[status2] + 1
            : 1;
        campaign = JSON.parse(JSON.stringify(campaign));
        campaign.status = status2;
        campaign.isActive = status2 === 'ACTIVE';

        let newCampaignsData = Array.from(campaignList.campaigns);
        newCampaignsData = newCampaignsData.map((data) => {
            if (data.status === status2) {
                const newData = Object.assign({}, data);
                newData.campaigns = [campaign, ...newData.campaigns];
                return newData;
            } else if (data.status === status1) {
                const newData = Object.assign({}, data);
                newData.campaigns = newData.campaigns.filter(
                    (c) => c.id !== campaign.id
                );
                return newData;
            } else {
                return data;
            }
        });

        dispatch(campaignsActions.setStatuses(newStatuses));
        dispatch(campaignsActions.setCampaigns(newCampaignsData));
    };

    const getLocationDataFromCampaign = async (campaign) => {
        const nodes = await getNodes();
        const selectedCountries = [];
        const selectedRegions = [];
        let selectedCities = [];
        const selectedNeighborhoods = [];
        const countries = [];
        const regions = [];
        const cities = [];
        const areaRegex = /Area/;
        const localityRegex = /Locality/;
        nodes.forEach((node) => {
            const country = { value: node.name, label: node.label };
            const isSelectedCountry = campaign.countries_selected.includes(
                country.value
            );
            if (isSelectedCountry) selectedCountries.push(country);
            countries.push(country);

            if (isSelectedCountry) {
                node.nodes.forEach((area) => {
                    if (areaRegex.test(area.type)) {
                        let region = { value: area.name, label: area.name };
                        let isSelectedRegion =
                            campaign.regions_selected.includes(region.value);
                        if (isSelectedRegion) selectedRegions.push(region);
                        regions.push(region);

                        area.nodes?.forEach((areaOrLocation) => {
                            if (
                                localityRegex.test(areaOrLocation.type) ||
                                (areaRegex.test(areaOrLocation.type) &&
                                    areaOrLocation.nodes)
                            ) {
                                region = {
                                    value: areaOrLocation.name,
                                    label: areaOrLocation.name,
                                };
                                let isSelectedRegion2 =
                                    campaign.regions_selected.includes(
                                        region.value
                                    );

                                if (isSelectedRegion || isSelectedRegion2) {
                                    if (areaOrLocation.nodes) {
                                        areaOrLocation.nodes.forEach(
                                            (location) => {
                                                let city;
                                                if (
                                                    campaign
                                                        .countries_selected[0] !==
                                                        'BR' &&
                                                    campaign
                                                        .countries_selected[0] !==
                                                        'MX'
                                                ) {
                                                    city = {
                                                        value: `${areaOrLocation.name} - ${location.name}`,
                                                        label: `${areaOrLocation.name} - ${location.name}`,
                                                    };
                                                } else {
                                                    city = {
                                                        value: areaOrLocation.name,
                                                        label: areaOrLocation.name,
                                                    };
                                                }
                                                const isSelectedCity =
                                                    campaign.cities_selected.includes(
                                                        city.value
                                                    );

                                                if (isSelectedCity) {
                                                    selectedCities.push(city);
                                                }
                                                cities.push(city);

                                                let neighborhood = {
                                                    value: location.name,
                                                    label: location.name,
                                                };
                                                const isSelectedNeighborhood =
                                                    campaign.neighborhoods_selected.includes(
                                                        neighborhood.value
                                                    );

                                                if (isSelectedNeighborhood) {
                                                    selectedNeighborhoods.push(
                                                        neighborhood
                                                    );
                                                }
                                            }
                                        );
                                    }
                                }
                            } else if (
                                localityRegex.test(areaOrLocation.type)
                            ) {
                                const city = {
                                    value: areaOrLocation.name,
                                    label: areaOrLocation.name,
                                };
                                const isSelectedCity =
                                    campaign.cities_selected.includes(
                                        city.value
                                    );
                                if (isSelectedCity) {
                                    selectedCities.push(city);
                                }

                                cities.push(city);

                                let neighborhood = {
                                    value: areaOrLocation.name,
                                    label: areaOrLocation.name,
                                };

                                selectedNeighborhoods.push(neighborhood);
                            }
                        });
                    }
                });
                let hash = {};
                selectedCities = selectedCities.filter((city) =>
                    hash[city.value] ? false : (hash[city.value] = true)
                );
            }
        });

        const zones = campaign.zones ? campaign.zones : [];

        return {
            nodes,
            countries,
            selectedCountries,
            regions,
            selectedRegions,
            cities,
            selectedCities,
            selectedNeighborhoods,
            zones,
        };
    };

    const getSegmentsFromCampaign = (campaign) => {
        const valuesAccuracy = ['low', 'mid', 'high'];

        const coverageIndexRaw = parseInt(campaign?.scopesi_coverage, 10);
        const coverage =
            Number.isNaN(coverageIndexRaw) || coverageIndexRaw < 0
                ? 1
                : Math.min(coverageIndexRaw, valuesAccuracy.length - 1);

        const {
            ages: agesRaw,
            gender,
            incomeLevel,
        } = getDemographicSelectionsFromCampaign(campaign);
        const age = {
            value: agesRaw,
            sliderValue: [0, 99],
        };

        if (agesRaw.length) {
            const parse = (v, pick) => {
                const s = String(v);
                const part =
                    pick === 'from' ? s.split('-')[0] : s.split('-').at(-1);
                const n = parseInt(part, 10);
                return Number.isFinite(n) ? n : null;
            };

            const from = parse(agesRaw[0], 'from') ?? 0;
            const to = parse(agesRaw.at(-1), 'to') ?? 99;

            age.sliderValue = [Math.min(from, to), Math.max(from, to)];
        }

        return {
            activeSegment: 'age',
            age,
            gender,
            incomeLevel,
            interests: [],
            accuracy: {
                value: valuesAccuracy[coverage] ?? valuesAccuracy[1],
                sliderValue: coverage,
            },
        };
    };

    const getTimingFromCampaign = async (campaign) => {
        const timezones = await getTimezones();
        let selectedTimezone = timezones.find(
            (timezone) => timezone.id === campaign.timezone_id
        );
        //if there's not selecteds start date or end date period value is setted as null
        if (selectedTimezone === null || selectedTimezone === undefined) {
            if (timezones.length > 0) {
                selectedTimezone = guessTimezoneOfBrowser(timezones);
            } else {
                selectedTimezone = '';
            }
        }
        let periodValue = [
            setOtherZone(new Date(), selectedTimezone.name),
            new Date().getTime(),
        ];
        if (campaign.start_period) {
            periodValue[0] = campaign.start_period;
        } else {
            periodValue = null;
        }

        if (campaign.end_period) periodValue[1] = campaign.end_period;
        else {
            periodValue = null;
        }

        const hoursEnabledFromApi =
            typeof campaign?.hours_enabled === 'string'
                ? campaign.hours_enabled
                : '';
        const campaignStrategy = resolveStrategyFromCampaignPayload(campaign);
        const campaignAlphaRaw =
            campaign?.alpha ?? campaign?.scheduler_alpha ?? 1.5;
        const campaignAlpha = Number(campaignAlphaRaw);
        return {
            daysEnabled: [],
            hoursEnabled: hoursEnabledFromApi,
            hoursOfWeek: [],
            isAsap: campaignStrategy === 'asap',
            strategy: campaignStrategy,
            alpha: Number.isFinite(campaignAlpha) ? campaignAlpha : 1.5,
            isCellSelected: {
                cells: [...Array(7)].map(() => Array(24).fill(false)),
            },
            isDayParting:
                typeof campaign?.is_day_parting === 'boolean'
                    ? campaign.is_day_parting
                    : hoursEnabledFromApi.length > 0 &&
                      hoursEnabledFromApi !== FULL_DAY_SCHEDULE,
            periodValue: periodValue,
            selectedTimezone: selectedTimezone ? selectedTimezone : '',
            timezones: timezones,
        };
    };

    const addMissingFieldsToCampaign = (campaign) => {
        const completeWith = (value, keys) =>
            keys.forEach(
                (key) => (campaign[key] = campaign[key] ? campaign[key] : value)
            );
        completeWith(
            [],
            [
                'selectedCountries',
                'selectedRegions',
                'selectedCities',
                'screens',
                'selectedCreativeCategories',
            ]
        );
        completeWith('', [
            'name',
            'ads_basename',
            'brand',
            'pixel_tracker_script',
        ]);
        completeWith(0, [
            'budget',
            'max_cpm',
            'cpm_optimize',
            'daily_limit',
            'limit_type',
            'auto_distribution',
        ]);
        completeWith(1, ['currency_id']);
    };

    const buildEditableCampaignData = async (campaign) => {
        campaign = JSON.parse(JSON.stringify(campaign));
        addMissingFieldsToCampaign(campaign);
        const fetchData = async () => {
            let creatives = getCreatives(campaign.id);
            let segments = getSegmentsFromCampaign(campaign);
            let timing = getTimingFromCampaign(campaign);

            const fullLang = window.navigator.language || 'en';
            const lang = fullLang.split('-')[0];

            let selectedCreativeCategories = await getCreativeCategories(
                'creative_categories' in campaign
                    ? campaign.creative_categories.filter(
                          (category) => category
                      )
                    : [],
                lang
            );

            // Validation for edge cases caused by a bug where the creative categories value is [1]
            if (
                selectedCreativeCategories.length === 0 ||
                selectedCreativeCategories.every((v) => v === undefined)
            ) {
                selectedCreativeCategories = [];
            }

            selectedCreativeCategories = await selectedCreativeCategories.map(
                (category) => category.uniqueId
            );

            creatives = await creatives;
            timing = await timing;

            return {
                creatives,
                segments,
                timing,
                selectedCreativeCategories,
            };
        };

        const { creatives, segments, timing, selectedCreativeCategories } =
            await fetchData();

        let selectedScreens = [];

        const selectedScreenIds = (campaign.screens ?? []).filter(Boolean);

        if (selectedScreenIds.length) {
            const externalIdsCsv = selectedScreenIds.join(',');
            const selectedScreensV3 = await getScreensPage({
                externalIds: externalIdsCsv,
                limit: selectedScreenIds.length,
            });
            selectedScreens = selectedScreensV3?.results || [];
        }
        selectedScreens = selectedScreens.filter((value) => value);

        // The saved selections are ids (names on legacy campaigns). WizardLocation
        // turns them into option objects once each geo level finishes loading.
        const buildStoredLocationSelection = () => ({
            nodes: [],
            countries: [],
            selectedCountries: normalizeStoredGeoSelection(
                campaign.countries_selected
            ),
            regions: [],
            selectedRegions: normalizeStoredGeoSelection(
                campaign.regions_selected
            ),
            cities: [],
            selectedCities: normalizeStoredGeoSelection(
                campaign.cities_selected
            ),
            neighborhood: [],
            selectedNeighborhoods: normalizeStoredGeoSelection(
                campaign.neighborhoods_selected
            ),
        });

        const emptyIfIsZero = (value) =>
            value === 0 || value === '0' ? '' : value;

        // NOTE: Audiences provider must be load here with the same key/data
        // from backend-v1 to work
        const providerOptions = {
            1: 'scopesi',
            2: 'cinnecta',
            3: 'onemata',
        };
        return {
            id: campaign.id,
            name:
                campaign.name !== t('sections.campaign-list.without-name')
                    ? campaign.name
                    : '',
            baseName: campaign.ads_basename || '',
            brand:
                campaign.brand !== t('sections.campaign-list.without-brand')
                    ? campaign.brand
                    : '',
            budget: {
                budget: numberWithoutCommas(campaign.budget),
                cpm: emptyIfIsZero(parseInt(campaign.max_cpm)),
                cpmOptimizer: campaign.cpm_optimize === 1,
            },
            reduction: {
                reduction: parseFloat(campaign.margin_red),
            },
            buyingRules: {
                isActive: false,
                buyIf: '',
                rules: [],
            },
            canLaunch: false,
            channels: {
                isDOOH: false,
                isMobile: false,
                isTV: false,
            },
            creatives,
            selectedCreativeCategories,
            currentPositionMap: null,
            currentWizard: 'basics',
            dailyCap: {
                budget: emptyIfIsZero(
                    numberWithoutCommas(campaign.daily_limit)
                ),
                impressions: emptyIfIsZero(
                    numberWithoutCommas(campaign.daily_limit)
                ),
                isActive: !(
                    parseInt(campaign.daily_limit) === 0 &&
                    0 === campaign.auto_distribution
                ),
                isBudget: campaign.limit_type === 1,
                isImpressions: campaign.limit_type === 0,
                isDistribution: campaign.auto_distribution === 1,
            },
            dailyCaps: {
                isActive: Object.keys(campaign.daily_caps ?? {}).length > 0,
                caps: campaign.daily_caps ?? {},
            },
            demographics: {
                id:
                    campaign.provider_audience &&
                    providerOptions[campaign.provider_audience],
            },

            errors: [],
            forecast: campaign.forecast
                ? campaign.forecast
                : {
                      targetUniverse: '-',
                      totalImpacts: '-',
                      coverage: '-',
                      coverageAbs: '-',
                      adImpressions: '-',
                      frequency: '-',
                      cpm: '-',
                      grp: '-',
                      screens: '-',
                      duration: '-',
                      validScreens: [],
                      screensNotUsed: [],
                      notMatchingScreens: [],
                      screensWithoutProvider: [],
                      screenCountWarnings: [],
                      screensUsed: [],
                      costs: '-',
                      isUnderspend: false,
                  },
            totalSpent: campaign.totalSpent,
            forecastTable: [],
            isResizeMapOn: false,
            expectedStats: normalizeExpectedStats(
                campaign.expected_stats_targets
            ),
            location: {
                ...buildStoredLocationSelection(),
                zones: campaign.zones || [],
            },
            mapLocations: {
                ...buildStoredLocationSelection(),
                zones: campaign.zones || [],
            },
            segments,
            selectedCurrency: currencies.find(
                (currency) => currency.id === campaign.currency_id
            ),
            selectedScreens,
            proximityFilterEnabled: resolveProximityFilterEnabled(
                getPersistedProximityFilterEnabled(campaign),
                campaign.zones
            ),
            providerSource: getCampaignProviderSource(
                campaign,
                selectedScreens
            ),
            sizesNeeded: [],
            stepper: { index: 0, wizard: 'basics' },
            timing,
            tracking: {
                isActive: !isEmpty(campaign.pixel_tracker_script),
                externalTracking: isEmpty(campaign.pixel_tracker_script)
                    ? ''
                    : campaign.pixel_tracker_script,
            },
            wasLaunched: false,
            status: campaignStatus(campaign.status),
            isTest: campaign.is_test,
            label_id: campaign.label_id,
        };
    };

    const editCampaign = async (campaign) => {
        const campaignData = await buildEditableCampaignData(campaign);
        await continueNewCampaign(campaignData);
        return campaignData;
    };

    const saveCampaignExpectedStatsTargets = async ({
        campaign,
        expectedStats,
    }) => {
        const normalizedExpectedStats = normalizeExpectedStats(expectedStats);
        const response = getData(
            await axios.put(
                `${endpoints.saveCampaignExpectedStatsTargetsV2}${campaign.id}/expected-stats-targets`,
                {
                    expectedStats: normalizedExpectedStats,
                }
            )
        );

        if (!response?.campaign) {
            throw new Error('Invalid response from server');
        }

        return response.campaign;
    };

    const getCreatives = async (campaignId) => {
        try {
            const url = `${endpoints.ceativesV2}/withScreens/${campaignId}`;
            const { data } = await axios.get(url);
            const creatives = data.data;

            //TODO: sync with new creative handler or delete
            // creatives.forEach((creative) => {
            //     const splited = creative.original_filename.split('.');
            //     let extension = splited[splited.length - 1];

            //     let isVast = creative.type === 8;
            //     let isScript = creative.type === 3;

            //     if (isVast) {
            //         extension = 'vast';
            //     }
            //     if (isScript) {
            //         extension = 'script';
            //     }

            //     creative.extension =
            //         extension.type !== '' ? extension : 'script';
            // });

            return creatives;
        } catch (e) {
            console.error({ e });
            dispatch(
                notificationsActions.setNotification({
                    type: 'warning',
                    title: t('common.notifications.error.title'),
                    message: t('common.notifications.error.errorData'),
                })
            );
        }
    };

    const removeZerosInDaysHours = (stats) => {
        const removeZerosIn = (key) => {
            const daysHours = {};
            // FIXME: Check if stats[key] is not null or undefined
            // OJO
            if (stats[key]) {
                Object.keys(stats[key]).forEach((currencyName) => {
                    daysHours[currencyName] = {};
                    Object.keys(stats[key][currencyName]).forEach((dayHour) => {
                        daysHours[currencyName][parseInt(dayHour)] =
                            stats[key][currencyName][dayHour];
                    });
                });
            }
            return daysHours;
        };
        stats.day = removeZerosIn('day');
        stats.hour = removeZerosIn('hour');

        return stats;
    };

    const saveCreativeCategoriesInDbAndLocalStorage = (
        categories,
        lang = 'en'
    ) => {
        try {
            if (!Array.isArray(categories)) {
                console.error(
                    'saveCreativeCategories: expected array, got:',
                    categories
                );
                return;
            }

            const cacheKey = `categories_${lang}`;
            const timeoutKey = `categoriesCacheTimeout_${lang}`;

            localStorage.setItem(timeoutKey, Date.now().toString());

            db.categories
                .bulkPut(categories)
                .catch((err) => console.error(err.message));

            localStorage.setItem(
                cacheKey,
                LZString.compress(JSON.stringify(categories))
            );
        } catch (err) {
            console.error('Error saving categories in DB/localStorage:', err);
        }
    };

    const fetchCreativeCategories = async (lang) => {
        try {
            const response = await fetch(
                `${endpoints.getAdCategories}?lang=${encodeURIComponent(lang)}`
            );

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const getCreativeCategories = async (
        optionalCategories = null,
        lang = 'en'
    ) => {
        try {
            const cacheKey = `categories_${lang}`;
            const timeoutKey = `categoriesCacheTimeout_${lang}`;
            const hour = 1000 * 60 * 60;

            const cached = LZString.decompress(localStorage.getItem(cacheKey));

            const cachedTimestamp = localStorage.getItem(timeoutKey) || 0;

            if (cached && Date.now() < Number(cachedTimestamp) + hour) {
                const categories = JSON.parse(cached);

                if (optionalCategories) {
                    return optionalCategories.map((id) =>
                        categories.find((cat) => cat.uniqueId === Number(id))
                    );
                }

                return categories;
            }

            // Fetch from API
            const response = await fetchCreativeCategories(lang);
            const categories = response?.adCategories || [];

            // Save to IndexedDB + localStorage (helper now handles everything)
            await saveCreativeCategoriesInDbAndLocalStorage(categories, lang);

            if (optionalCategories) {
                return optionalCategories.map((id) =>
                    categories.find((cat) => cat.uniqueId === Number(id))
                );
            }

            return categories;
        } catch (e) {
            console.error({ e });

            dispatch(
                notificationsActions.setNotification({
                    type: 'warning',
                    title: t('common.notifications.error.title'),
                    message: t('common.notifications.error.errorData'),
                })
            );

            return [];
        }
    };

    const formatTotals = (objectTotals, currencySymbol) => {
        if (!objectTotals) {
            return;
        }

        let newObject;
        if (!currencySymbol) {
            newObject = {
                spent: formatNumber(objectTotals.spent, 'currency'),
                ecpm: formatNumber(objectTotals.ecpm, 'currency'),
                impacts: formatNumber(objectTotals.impacts, 'rounded'),
                impressions: formatNumber(objectTotals.impressions, 'rounded'),
                spot: formatNumber(objectTotals.spot, 'currency'),
            };
        } else {
            newObject = {
                spent: formatNumber(
                    objectTotals.spent,
                    'properCurrency',
                    null,
                    { currency: currencySymbol }
                ),
                ecpm: formatNumber(objectTotals.ecpm, 'properCurrency', null, {
                    currency: currencySymbol,
                }),
                impacts: formatNumber(objectTotals.impacts, 'rounded'),
                impressions: formatNumber(objectTotals.impressions, 'rounded'),
                spot: formatNumber(objectTotals.spot, 'properCurrency', null, {
                    currency: currencySymbol,
                }),
            };
        }

        return newObject;
    };

    const getStatsByUser = async (from, to, rawData) => {
        try {
            const userId = session.user.id;

            let promiseStatsByCampaignsHistory =
                statsService.getStatsOfCampaignsByUserIdHistory(userId);

            let promiseStatsByCampaigns =
                statsService.getStatsOfCampaignsByUserId(
                    from,
                    to,
                    rawData,
                    userId
                );

            let promiseStatsBydays = statsService.getStatsByDay(
                from,
                to,
                userId
            );
            let promiseStatsByBrands = statsService.getStatsBrandsByUserId(
                from,
                to,
                userId
            );
            let promiseStatsByHours = statsService.getStatsByHours(
                from,
                to,
                userId
            );
            let [
                statsByCampaigns,
                statsByDays,
                statsByHours,
                statsByBrands,
                statsByCampaignsHistory,
            ] = await Promise.allSettled([
                promiseStatsByCampaigns,
                promiseStatsBydays,
                promiseStatsByHours,
                promiseStatsByBrands,
                promiseStatsByCampaignsHistory,
            ]);
            statsByCampaignsHistory = statsByCampaignsHistory.value.data.stats;

            //set acumulated data for actives campaigns
            dispatch(
                dashboardActions.setReportsForActiveCampaigns(
                    statsByCampaignsHistory
                )
            );

            statsByCampaigns = statsByCampaigns.value.data;
            statsByDays = statsByDays.value.data;
            statsByBrands = statsByBrands.value.data;
            statsByHours = statsByHours.value.data;
            let response = {};

            const currenciesOfStats = []; //save all different currencies from stats and set the stats for each currency
            // totals for dashboard top
            Object.entries(statsByCampaigns.totals).forEach(
                ([currencyShort, totalByCurrency]) => {
                    const currencyData = getCurrencyByName(currencyShort);
                    const totals = formatTotals({ ...totalByCurrency }, '');
                    totals.overBudget =
                        (totalByCurrency.spentAllCampaigns /
                            totalByCurrency.budgetAllCampaigns) *
                        100;
                    totals.budget = formatNumber(
                        totalByCurrency.budgetAllCampaigns,
                        'currency'
                    );
                    totals.amountActiveCampaigns =
                        totalByCurrency.amountActiveCampaigns;
                    currenciesOfStats.push({
                        currency: currencyData,
                        stats: totals,
                    });
                }
            );
            response.currenciesOfStats = currenciesOfStats;
            statsByCampaigns.currencies.forEach((shortName) => {
                const dataCurrency = getCurrencyByName(shortName);
                // format totals for down table
                statsByCampaigns.totals[shortName] = formatTotals(
                    statsByCampaigns.totals[shortName],
                    dataCurrency.short_name
                );
                statsByDays.totals[shortName] = formatTotals(
                    statsByDays.totals[shortName],
                    dataCurrency.short_name
                );
                statsByHours.totals[shortName] = formatTotals(
                    statsByHours.totals[shortName],
                    dataCurrency.short_name
                );
                statsByBrands.totals[shortName] = formatTotals(
                    statsByBrands.totals[shortName],
                    dataCurrency.short_name
                );

                // add dates and properties
                statsByDays.stats[shortName].forEach((stat) => {
                    moment.locale(window.navigator.language);
                    let dateWithMonth = moment(stat.date, 'YYMMDDHH').format(
                        'L'
                    );
                    dateWithMonth = dateWithMonth.substring(0, 5);
                    stat.dateWithMonth = dateWithMonth;
                    stat.day = dateWithMonth;
                    stat.name = dateWithMonth;
                    stat.spot = stat.spot;
                    stat.days = stat.name;
                });

                // add links for table TO STATS BY CAMPAIGNS
                statsByCampaigns.stats[shortName].forEach((statByCampaign) => {
                    statByCampaign.link = `/dashboard/${statByCampaign.campaignid}`;
                    statByCampaign.id = statByCampaign.campaignid;
                    statByCampaign.name = statByCampaign.campaignname;
                });
            });

            const dashboardGeneralReports = [
                {
                    tabName: t('common.words.campaigns'),
                    columnName: t('common.words.campaign'),
                    data: statsByCampaigns.stats,
                },
                {
                    tabName: capitalizeFirstLetter(t('common.words.days')),
                    columnName: capitalizeFirstLetter(t('common.words.day')),
                    data: statsByDays.stats,
                },
                {
                    tabName: t('common.words.hours'),
                    columnName: t('common.words.hour'),
                    data: statsByHours.stats,
                },
                {
                    tabName: t('common.words.brands'),
                    columnName: t('common.words.brand'),
                    data: statsByBrands.stats,
                },
                {
                    currencies: statsByCampaigns.currencies,
                },
            ];
            dispatch(
                dashboardActions.setDashboardGeneralReports(
                    dashboardGeneralReports
                )
            );
            dispatch(dashboardActions.setCampaignStats(response));
            return [
                statsByCampaigns.totals,
                statsByDays.totals,
                statsByHours.totals,
                statsByBrands.totals,
            ];
        } catch (e) {
            console.error({ e });
        }
    };

    const getStatsByDay = async (from, to) => {
        try {
            from = new Date(from);
            from = formatDate(from);
            to = new Date(to);
            to = formatDate(to);
            let url = `${endpoints.getStats}/days/user/${session.user.id}?from=${from}&to=${to}`;
            const response = await axios.get(url);

            return response;
        } catch (e) {
            console.error(e);
        }
    };

    const getStatsByCampaign = async (campaignId, from, to) => {
        try {
            from = new Date(from);
            from = formatDate(from);
            to = new Date(to);
            to = formatDate(to);
            let url = `${endpoints.getStats}/days/campaign/${campaignId}?from=${from}&to=${to}`;
            const response = await axios.get(url);

            return response;
        } catch (e) {
            console.error(e);
        }
    };

    const getStatsByCampaignByHours = async (campaignId, from, to) => {
        try {
            from = new Date(from);
            from = formatDate(from);
            to = new Date(to);
            to = formatDate(to);
            let url = `${endpoints.getStats}/hours/campaign/${campaignId}?from=${from}&to=${to}`;
            const response = await axios.get(url);

            return response;
        } catch (e) {
            console.error(e);
        }
    };

    const getCurrencyById = (id) => {
        return currencies.find((currency) => currency.id === parseInt(id));
    };

    const getCurrencyByName = (name) => {
        return currencies.find((currency) => currency.short_name === name);
    };

    const parseRolToString = (rol) => {
        const translations =
            'sections.management.section.users.users.createUserModal.';
        switch (rol) {
            case 1:
                rol = t(translations + 'roles.admin');
                break;
            case 2:
                rol = t(translations + 'roles.manager');
                break;
            case 3:
                rol = t(translations + 'roles.advertiser');
                break;
            case 4:
                rol = t(translations + 'roles.slave');
                break;
            case 5:
                rol = t(translations + 'roles.supervisor');
                break;
            default:
                break;
        }
        return rol;
    };

    const parseMargin = (margin) => {
        const MARGIN_LENGTH = 10;
        return margin.toFixed(MARGIN_LENGTH);
    };

    const getUsers = async (
        username = '',
        // FIXME: limit is not used, it should use env REACT_APP_MAX_USERS_PER_DROPDOWN
        limit = null,
        page = 1
    ) => {
        const parseRoles = (data) => {
            data.forEach((user) => {
                user.roleNumber = user.role;
                user.role = parseRolToString(user.role);
            });

            return data;
        };

        try {
            const params = {};

            if (limit) {
                params.limit = limit;
                params.page = page;
            }

            if (username !== '') {
                params.username = username;
            }
            let response = getData(
                await axios.get(endpoints.getUsers, { params })
            );
            response = JSON.parse(JSON.stringify(response));
            if (limit) parseRoles(response.data);
            else parseRoles(response);

            return response;
        } catch (e) {
            console.error({
                type: 'error',
                title: 'Error',
                message: `Statuses Error: ${e}`,
            });
            dispatch(
                notificationsActions.setNotification({
                    type: 'warning',
                    title: t('common.notifications.error.title'),
                    message: t('common.notifications.error.errorData'),
                })
            );
        }
    };

    const getUserById = async (id) => {
        const user = getData(await axios.get(endpoints.getUserById + id));
        return user;
    };

    const addBilling = async (user, currency, billing, transactionType) => {
        try {
            const body = {
                userId: user.id,
                currencyId: currency.id,
                transactionType: transactionType,
                billing,
            };
            await axios.post(endpoints.addBillingV2, body);

            dispatch(
                notificationsActions.setNotification({
                    type: 'success',
                    title: t(
                        'sections.management.section.billing.funding.title'
                    ),
                    message: t(
                        'sections.management.section.billing.funding.successfulMessage'
                    ),
                })
            );
        } catch (error) {
            ErrorMessage(error);
        }
    };

    const isValidUsername = async (username) => {
        if (username.trim() === '') return false;
        try {
            let url = `${endpoints.getUsers}?limit=1&username=${username}&page=1`;
            const response = getData(await axios.get(url));
            if (response.data.length === 1) {
                const [user] = response.data;
                return user.username.toLowerCase() !== username.toLowerCase();
            } else {
                return true;
            }
        } catch (e) {
            console.error({ e });
            dispatch(
                notificationsActions.setNotification({
                    type: 'warning',
                    title: t('common.notifications.error.title'),
                    message: t('common.notifications.error.errorData'),
                })
            );
            return false;
        }
    };

    const getCompanies = async () => {
        const companies = getData(await axios.get(endpoints.getCompanies));
        return companies;
    };

    const isValidEditUsername = async (username) => {
        if (username === '') return false;
        try {
            let url = `${endpoints.getUsers}?limit=1&username=${username}&page=1`;
            const response = getData(await axios.get(url));
            if (response.data.length === 1) {
                const [user] = response.data;
                return user.username.toLowerCase() !== username.toLowerCase();
            } else {
                return true;
            }
        } catch (e) {
            console.error({ e });
            dispatch(
                notificationsActions.setNotification({
                    type: 'warning',
                    title: t('common.notifications.error.title'),
                    message: t('common.notifications.error.errorData'),
                })
            );
            return false;
        }
    };

    const formDataToBodyParams = (formData) => {
        let parentAccountId;
        let role;

        if (formData.isParentAccount) {
            role = 4;
            parentAccountId = formData.userSelected.id;
        } else {
            parentAccountId = null;
            role = stringToRoleUser(formData.roleSelected.value);
        }

        const type = stringToTypeUser(formData.typeUser.value);
        const data = {
            username: formData.username,
            password: formData.wantChangePassword ? formData.password : '',
            name: formData.name,
            type: type,
            email: formData.email,
            role: role,
            parent_id: parentAccountId,
            margin: parseFloat(formData.margin),
            company_id: formData.hasCompany ? formData.company.id : 1,
            show_ooh_option: formData.showOohOption ? 1 : 0,
            cpm_mode: formData.cpm_mode ? 1 : 0,
            phone_number: formData.phoneNumber,
            rebate: parseFloat(formData.rebate),
        };

        data.country_id = formData.countrySelected?.id || 1;
        if (formData.companyName) data.company = formData.companyName;
        if (formData.businessName) data.business_name = formData.businessName;
        if (formData.taxId) data.tax_id = formData.taxId;
        if (formData.address) data.address = formData.address;

        return data;
    };

    const createUser = async (formData) => {
        try {
            const bodyParams = formDataToBodyParams(formData);
            const response = await axios.post(endpoints.createUser, bodyParams);
            if (!response.data.error) {
                const userCreated = getData(response);
                formData.ssps.forEach((ssp) => {
                    assignSspToUser(userCreated.id, ssp);
                });
                dispatch(
                    notificationsActions.setNotification({
                        type: 'success',
                        title: t(
                            'sections.management.section.users.users.createUserModal.userCreatedTitle'
                        ),
                        message: t(
                            'sections.management.section.users.users.createUserModal.userCreatedMessage'
                        ),
                    })
                );
                return {
                    ...userCreated,
                    role: parseRolToString(userCreated.role),
                    rtb_margin: parseMargin(userCreated.rtb_margin),
                };
            } else {
                console.error(response.data.errors);
                dispatch(
                    notificationsActions.setNotification({
                        type: 'warning',
                        title: t('common.notifications.error.title'),
                        message: t('common.notifications.error.errorData'),
                    })
                );
            }
        } catch (e) {
            console.error({ e });
            dispatch(
                notificationsActions.setNotification({
                    type: 'warning',
                    title: t('common.notifications.error.title'),
                    message: t('common.notifications.error.errorData'),
                })
            );
            return false;
        }
    };

    const editUser = async (formData, userId) => {
        try {
            const bodyParams = formDataToBodyParams(formData);
            const response = await axios.put(
                endpoints.editUser + userId,
                bodyParams
            );
            asignSspToUser(userId, formData.ssps);
            let newUser = getData(response);
            if (!response.data.error) {
                dispatch(
                    notificationsActions.setNotification({
                        type: 'success',
                        title: t(
                            'sections.management.section.users.users.createUserModal.userEditedTitle'
                        ),
                        message: t(
                            'sections.management.section.users.users.createUserModal.userEditedMessage'
                        ),
                    })
                );
                newUser.roleNumber = newUser.role;
                newUser.role = parseRolToString(newUser.role);
                newUser.role = parseRolToString(newUser.role);
                newUser.rtb_margin = parseMargin(newUser.rtb_margin);
            } else {
                dispatch(
                    notificationsActions.setNotification({
                        type: 'error',
                        message: t('common.notifications.error.errorData'),
                    })
                );
                return false;
            }
            return newUser;
        } catch (e) {
            console.error({ e });
            dispatch(
                notificationsActions.setNotification({
                    type: 'error',
                    message: t('common.notifications.error.errorData'),
                })
            );
            return false;
        }
    };

    const getSsps = async () => {
        try {
            const ssp = getData(await axios.get(endpoints.getSsps));
            return ssp;
        } catch (error) {
            console.error(`Error in ssp ${error}`);
        }
    };

    const getSspsByUser = async (userId) => {
        try {
            let url = `${endpoints.userSsp}${userId}/ssp`;
            let response = getData(await axios.get(url));
            return response;
        } catch (e) {
            console.error({ e });
            dispatch(
                notificationsActions.setNotification({
                    type: 'warning',
                    title: t('common.notifications.error.title'),
                    message: t('common.notifications.error.errorData'),
                })
            );
        }
    };

    const assignSspToUser = async (userId, ssp) => {
        const url = endpoints.assignSspToUser + userId + '/ssp';
        const response = await axios.post(url, { name: ssp.name });
        return getData(response);
    };

    const asignSspToUser = async (userId, userSsps) => {
        try {
            let url = `${endpoints.asignSsps}${userId}/ssp`;
            const body = { ssps: userSsps };
            let response = getData(await axios.put(url, body));
            return response;
        } catch (e) {
            console.error({ e });
            dispatch(
                notificationsActions.setNotification({
                    type: 'warning',
                    title: t('common.notifications.error.title'),
                    message: t('common.notifications.error.errorData'),
                })
            );
        }
    };

    const getCountries = async () => {
        try {
            const countries = getData(
                await axios.get(endpoints.getCountriesV2)
            );
            return countries;
        } catch (error) {
            ErrorMessage(error);
        }
    };

    const getCommercialPersonsAudit = async (userId) => {
        try {
            let url = `${endpoints.getCommercialContacts}${userId}/company-person`;
            let response = getData(await axios.get(url));
            return response;
        } catch (e) {
            console.error({ e });
            dispatch(
                notificationsActions.setNotification({
                    type: 'warning',
                    title: t('common.notifications.error.title'),
                    message: t('common.notifications.error.errorData'),
                })
            );
        }
    };

    const addCommercialPersonsAudit = async (userId, commercialPerson) => {
        let createdCommercialPerson = getData(
            await axios.post(
                `${endpoints.getCommercialContacts}${userId}/company-person/assign`,
                commercialPerson
            )
        );

        return createdCommercialPerson;
    };

    const removeCommercialPersonAudit = async (userId, commercialPersonId) => {
        const url = `${endpoints.getCommercialContacts}${userId}/company-person/${commercialPersonId}`;
        await axios.delete(url);
        dispatch(
            commercialsPersonsActions.removeCommercialPerson(commercialPersonId)
        );
        return true;
    };

    const editCommercialPersonAudit = async (userId, commercialPerson) => {
        const url = `${endpoints.getCommercialContacts}${userId}/company-person/edit`;
        let commercialPersonEdited = getData(
            await axios.put(url, commercialPerson)
        );
        // commercialPersonEdited.phoneNumber = commercialPersonEdited.phone_number
        // delete commercialPersonEdited.phone_number
        // dispatch(commercialsPersonsActions.editCommercialPerson(commercialPersonEdited))
        return commercialPersonEdited;
    };

    const assignBudgetToCampaign = async (campaignId, newBudget) => {
        try {
            const url = `${endpoints.assignBudgetToCampaignV2}${campaignId}/budget/${newBudget}`;
            const campaign = getData(await axios.put(url));
            const currencies = await getCurrencies();
            const campaignCurrency = currencies.find(
                (currency) => currency.id === campaign.currency_id
            );
            campaign.currency = campaignCurrency;
            updateBudgets();
            return campaign;
        } catch (error) {
            ErrorMessage(error);
        }
    };

    /**
     * Add one commercial person to one user
     * @param {CommercialPerson} commercialPerson with name, email, phoneNumber, role
     * @returns {CommercialPerson} created
     */
    const addCommercialPersonToUser = async (commercialPerson) => {
        let createdCommercialPerson = getData(
            await axios.post(endpoints.createCommercialPerson, commercialPerson)
        );
        createdCommercialPerson.phoneNumber =
            createdCommercialPerson.phone_number;
        delete createdCommercialPerson.phone_number;
        dispatch(
            commercialsPersonsActions.addCommercialPerson(
                createdCommercialPerson
            )
        );
        return createdCommercialPerson;
    };

    /**
     * Remove one commercial person from one user
     * @param {Number} commercialPersonId id of the commercial person to remove
     * @returns {Boolean}
     */
    const removeCommercialPersonToUser = async (commercialPersonId) => {
        const url = endpoints.removeCommercialPerson + commercialPersonId;
        await axios.delete(url);
        dispatch(
            commercialsPersonsActions.removeCommercialPerson(commercialPersonId)
        );
        return true;
    };

    /**
     * Get all commercial persons of one user
     * @returns {Array} of commercial persons
     */
    const getCommercialsPersons = async () => {
        let commercialPersons = getData(
            await axios.get(endpoints.getCommercialsPersons)
        );
        commercialPersons = commercialPersons.map((cp) => {
            cp.phoneNumber = cp.phone_number;
            delete cp.phone_number;
            return cp;
        });
        dispatch(
            commercialsPersonsActions.setCommercialsPersons(commercialPersons)
        );
        return commercialPersons;
    };

    /**
     * Edit one commercial person of one user
     * @param {CommercialPerson} commercialPerson to edit with id and data
     * @returns {CommercialPerson} edited
     */
    const editCommercialPerson = async (commercialPerson) => {
        const url = endpoints.editCommercialPerson + commercialPerson.id;
        let commercialPersonEdited = getData(
            await axios.put(url, commercialPerson)
        );
        commercialPersonEdited.phoneNumber =
            commercialPersonEdited.phone_number;
        delete commercialPersonEdited.phone_number;
        dispatch(
            commercialsPersonsActions.editCommercialPerson(
                commercialPersonEdited
            )
        );
        return commercialPersonEdited;
    };

    const changeDailyCapLimit = async (campaignId, newDailyCapLimit) => {
        try {
            const url = `${endpoints.assignDailyCapLimitToCampaignV2}${campaignId}/daily-cap/${newDailyCapLimit}`;
            const campaign = getData(await axios.put(url));
            const currencies = await getCurrencies();
            const campaignCurrency = currencies.find(
                (currency) => currency.id === campaign.currency_id
            );
            campaign.currency = campaignCurrency;
            return campaign;
        } catch (error) {
            ErrorMessage(error);
        }
    };

    const changeCpm = async (campaignId, newCpm) => {
        try {
            const url = `${endpoints.assignCpmToCampaignV2}${campaignId}/cpm/${newCpm}`;
            const campaign = getData(await axios.put(url));
            const currencies = await getCurrencies();
            const campaignCurrency = currencies.find(
                (currency) => currency.id === campaign.currency_id
            );
            campaign.currency = campaignCurrency;
            dispatch(
                notificationsActions.setNotification({
                    type: 'success',
                    title: t(
                        'common.notifications.success.cpmChangeSuccessfull-title'
                    ),
                    message: t(
                        'common.notifications.success.cpmChangeSuccessfull-description'
                    ),
                })
            );
            return campaign;
        } catch (error) {
            ErrorMessage(error);
        }
    };

    const getInvoicesForUser = async (from = '', to = '') => {
        const isAdmin = auth.isAdmin();
        const url = isAdmin
            ? endpoints.getAdminInvoices + `?from=${from}&to=${to}`
            : endpoints.getUserInvoices + `?from=${from}&to=${to}`;

        const invoices = getData(await axios.get(url));
        return invoices;
    };

    const ErrorMessage = (error) => {
        if (
            !isNaN(error.response?.data?.errorCode) &&
            error.response?.data?.errorCode > 1000
        ) {
            dispatch(
                notificationsActions.setNotification({
                    type: 'warning',
                    title: t('common.notifications.error.title'),
                    message: t('errorCode.' + error.response.data.errorCode),
                })
            );
        } else {
            dispatch(
                notificationsActions.setNotification({
                    type: 'warning',
                    title: t('common.notifications.error.title'),
                    message: t('common.notifications.error.errorData'),
                })
            );
        }
        return;
    };

    const createCampaignProposalName = async () => {
        const supervisorSession = JSON.parse(
            localStorage.getItem('supervisorSession')
        );
        const session = JSON.parse(localStorage.getItem('session'));
        if (!supervisorSession || !session) {
            throw new Error('Session data is missing');
        }
        const name = '';
        const brand = '';
        const url = endpoints.campaignProposalCrud;
        const token = supervisorSession.token;
        const admin_id = supervisorSession.user.id;
        const user_id = session.user.id;
        const payload = {
            name,
            brand,
            admin_id,
            user_id,
        };
        try {
            const response = await axios.post(url, payload, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });
            history.push(`/proposal/${response.data.data.id}`);
            return response.data.data.id;
        } catch (error) {
            throw error;
        }
    };

    const fetchCampaignProposalsByStatus = async (status) => {
        const supervisorSession = JSON.parse(
            localStorage.getItem('supervisorSession')
        );
        const session = JSON.parse(localStorage.getItem('session'));
        if (!supervisorSession || !session) {
            throw new Error('Session data is missing');
        }

        const token = supervisorSession.token;
        const admin_id = supervisorSession.user.id;
        const user_id = session.user.id;

        try {
            const response = await axios.get(
                `${endpoints.getCampaignProposalsByStatus}/${user_id}/${status}`,

                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            return response.data.data;
        } catch (error) {
            throw error;
        }
    };

    const getCampaignProposalById = async (id) => {
        const supervisorSession = JSON.parse(
            localStorage.getItem('supervisorSession')
        );
        const token = supervisorSession.token;
        const url = endpoints.getCampaignProposalById(id);
        try {
            const response = await axios.get(url, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    };

    const updateCampaignProposal = async (payload) => {
        const supervisorSession = JSON.parse(
            localStorage.getItem('supervisorSession')
        );
        const url = endpoints.campaignProposalCrud;
        const token = supervisorSession.token;
        try {
            const response = await axios.put(url, payload, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    };

    const createLabel = async (payload) => {
        const supervisorSession = JSON.parse(
            localStorage.getItem('supervisorSession')
        );
        const url = endpoints.createLabel;
        const token = supervisorSession.token;
        try {
            const response = await axios.post(url, payload, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    };
    const getLabelsByCampaignId = async (id) => {
        const supervisorSession = JSON.parse(
            localStorage.getItem('supervisorSession')
        );
        const url = endpoints.getLabelsByCampaignId(id);
        const token = supervisorSession.token;
        try {
            const response = await axios.get(url, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    };

    const getCampaignByUserIdAndStatus = async (status) => {
        try {
            const url = endpoints.getCampaignByUserIdAndStatus + status;
            const response = await axios.get(url);
            return response.data;
        } catch (error) {
            throw error;
        }
    };

    const deleteCampaignProposal = async (id) => {
        const supervisorSession = JSON.parse(
            localStorage.getItem('supervisorSession')
        );
        const url = endpoints.campaignProposalCrud;
        const token = supervisorSession.token;
        try {
            const response = await axios.delete(`${url}/${id}`, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    };

    const createCampaign = async (campaignName = '', campaignBrand = '') => {
        dispatch(newCampaignActions.reset());
        dispatch(screensActions.resetByKey('maxCpm'));
        dispatch(screensActions.resetByKey('selectedScreensLocal'));
        dispatch(screensActions.resetByKey('isFilterByAudiences'));
        dispatch(screensActions.resetByKey('showSelectedScreens'));
        dispatch(screensActions.resetByKey('selectedScreenTypes'));
        dispatch(screensActions.resetByKey('selectedPublishers'));
        dispatch(screensActions.resetByKey('selectedTiers'));
        resetForecast();

        const data = formatValuesSavePreview({
            ...newCampaignInitialState,
            forecast: DEFAULT_FORECAST,
        });
        data.name = campaignName;
        data.brand = campaignBrand;

        try {
            const response = getData(
                await axios.post(endpoints.saveCampaignPreviewV2, data)
            );

            if (response.campaign) {
                const id = response.campaign.id;
                const statusCampaign = campaignStatus(response.campaign.status);

                if (id != newCampaign.id) {
                    dispatch(newCampaignActions.setId(id));
                    dispatch(newCampaignActions.setStatus(statusCampaign));

                    updateCampaignInListOfCampaigns(response.campaign);

                    if (statusCampaign != 'DRAFT') {
                        await updateBudgets();
                    }
                }
            }

            return response;
        } catch (error) {
            ErrorMessage(error);
        }
    };

    const linkLabelAndSubcampaign = async (payload) => {
        const supervisorSession = JSON.parse(
            localStorage.getItem('supervisorSession')
        );
        const url = endpoints.linkLabelAndSubcampaign;
        const token = supervisorSession.token;
        try {
            const response = await axios.put(url, payload, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    };

    const generateProposals = async (id) => {
        const supervisorSession = JSON.parse(
            localStorage.getItem('supervisorSession')
        );
        const url = endpoints.generateProposals(id);
        const token = supervisorSession.token;
        try {
            const response = await axios.get(url, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });
            successNotification(
                t('common.notifications.success.proposalGenerated')
            );
            return response;
        } catch (err) {
            warningNotification(
                t('common.notifications.warning.proposalGeneratedError'),
                t('common.notifications.warning.proposalForecast')
            );
        }
    };
    const getLinkedCampaigns = async (id) => {
        const supervisorSession = JSON.parse(
            localStorage.getItem('supervisorSession')
        );
        const url = endpoints.getLinkedCampaigns(id);
        const token = supervisorSession.token;
        try {
            const response = await axios.get(url, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });
            return response;
        } catch {}
    };
    const getProposalBrief = async (id) => {
        const supervisorSession = JSON.parse(
            localStorage.getItem('supervisorSession')
        );
        const token = supervisorSession.token;
        const url = endpoints.getProposalBrief + id;
        try {
            const response = await axios.get(url, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    };
    const getProposalsById = async (id) => {
        const supervisorSession = JSON.parse(
            localStorage.getItem('supervisorSession')
        );
        const token = supervisorSession.token;
        const url = endpoints.getProposals(id);
        try {
            const response = await axios.get(url, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    };

    const sendEmailWithProposal = async (id, email) => {
        const supervisorSession = JSON.parse(
            localStorage.getItem('supervisorSession')
        );

        const url = `${endpoints.sendProposalEmail}${id}?email=${email}`;
        const token = supervisorSession.token;

        try {
            const response = await axios.post(
                url,
                {},
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            successNotification(t('common.notifications.success.proposalSent'));

            return response.data;
        } catch (error) {
            warningNotification(
                t('common.notifications.warning.proposalSentError')
            );
        }
    };

    const usoProposal = async (id) => {
        const supervisorSession = JSON.parse(
            localStorage.getItem('supervisorSession')
        );
        const token = supervisorSession.token;
        const url = endpoints.getProposals(id);
        try {
            const response = await axios.get(url, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    };

    const checkSubCampaignBeforeLaunch = async (id) => {
        const supervisorSession = JSON.parse(
            localStorage.getItem('supervisorSession')
        );
        const token = supervisorSession.token;
        const url = endpoints.checkSubCampaignBackUp + id;
        try {
            const response = await axios.get(url, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });
            const validations = response?.data?.data?.validations;

            if (validations.length) {
                let validationWithMessage = validations.map((validation) =>
                    t(
                        `common.campaign-proposals.check-differences.${validation.key}`
                    )
                );
                return validationWithMessage;
            } else {
                return null;
            }
        } catch (error) {
            return null;
        }
    };

    const unassignLabelByCampaignId = async (subcampaignId) => {
        const supervisorSession = JSON.parse(
            localStorage.getItem('supervisorSession')
        );
        const token = supervisorSession.token;
        const url = endpoints.unassignLabel + subcampaignId;
        try {
            const response = await axios.put(
                url,
                {},
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    };

    const getCampaignProposalCount = async () => {
        const supervisorSession = JSON.parse(
            localStorage.getItem('supervisorSession')
        );
        const session = JSON.parse(localStorage.getItem('session'));
        const token = supervisorSession.token;
        const id = session.user.id;
        const url = endpoints.getCampaignProposalCount + id;
        try {
            const response = await axios.get(url, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });
            return response.data;
        } catch {}
    };

    const getCountriesByCurrencyId = async (currencyId) => {
        const url = endpoints.getCountriesByCurrency + currencyId;
        try {
            const response = await axios.get(url);
            return response.data;
        } catch {}
    };

    const getRegionsByCountryIds = async (countryId) => {
        const url = endpoints.getRegionsByCountry(countryId);
        try {
            const response = await axios.get(url);
            return response.data;
        } catch {}
    };

    const getCitiesByRegionIds = async (regionId) => {
        const url = endpoints.getCitiesByRegion(regionId);
        try {
            const response = await axios.get(url);
            return response.data;
        } catch {}
    };

    const getAreasByCityIds = async (citiesIds) => {
        const url = endpoints.getAreasByCity(citiesIds);
        try {
            const response = await axios.get(url);
            return response.data;
        } catch {}
    };

    const adjustCampaignExpectedStats = async ({
        campaignId,
        expectedStats,
        apply = false,
    }) => {
        try {
            const session = JSON.parse(localStorage.getItem('session'));
            const supervisorSession = session?.isSupervised
                ? JSON.parse(localStorage.getItem('supervisorSession'))
                : null;
            const config = supervisorSession?.token
                ? {
                      headers: {
                          Authorization: `Bearer ${supervisorSession.token}`,
                      },
                  }
                : undefined;
            const response = await axios.post(
                endpoints.adjustCampaignStatsTargets,
                {
                    campaignId,
                    targets: normalizeExpectedStats(expectedStats),
                    apply,
                },
                config
            );

            if (!response.data) {
                throw new Error('Invalid response from server');
            }

            return {
                changedRows: response.data?.changedRows ?? 0,
                missingTargetScreens: Array.isArray(
                    response.data?.missingTargetScreens
                )
                    ? response.data.missingTargetScreens
                    : [],
                verification: Array.isArray(response.data?.verification)
                    ? response.data.verification
                    : [],
            };
        } catch (error) {
            console.error('Expected stats sync error:', error);
            throw error;
        }
    };

    return {
        getUserMovements,
        uploadTester,
        getCreativeType,
        getCreativeCategories,
        getBudgets,
        assignBudgetToCampaign,
        changeDailyCapLimit,
        changeCpm,
        getBudgetsDetails,
        getUsers,
        addBilling,
        isValidUsername,
        createUser,
        getCreatives,
        getCampaignPreview,
        getTimezones,
        getCampaigns,
        activateCampaign,
        deactivateCampaign,
        restoreCampaign,
        deleteCampaign,
        duplicateCampaign,
        editCampaign,
        getCampaignStats,
        getCampaignPrediction,
        getStatsByUser,
        getCurrencyById,
        getCampaignInLocalStorage,
        clearNewCampaignData,
        onClickNextPageOfCampaignsList,
        onClickPreviusPageOfCampaignsList,
        fetchMoreDataOfCurrentCampaignList,
        getCampaignsPerStatus,
        getCurrencies,
        getScreens,
        getScreenProviders,
        getScreenProvidersFiltered,
        saveOrUpdateCampaignPreview,
        continueNewCampaign,
        launchCampaign,
        getMargins,
        uploadFiles,
        uploadScript,
        vastInDisplay,
        duplicateCreative,
        removeCreative,
        removeMultipleCreatives,
        updateCPMOfScreens,
        changeResolution,
        getProviderMetadata,
        calculateForecast,
        getForecastRequestKey,
        getNodes,
        forgotPassword,
        resetPassword,
        editUser,
        getSspsByUser,
        getSsps,
        asignSspToUser,
        isValidEditUsername,
        parseRolToString,
        getCountries,
        getCompanies,
        getUserById,
        getCommercialsPersons,
        getCommercialPersonsAudit,
        addCommercialPersonsAudit,
        removeCommercialPersonAudit,
        removeCommercialPersonToUser,
        editCommercialPersonAudit,
        addCommercialPersonToUser,
        editCommercialPerson,
        getInvoicesForUser,
        getScreensReports,
        ErrorMessage,
        setDraftCampaignAutodelete,
        getUserSettings,
        getScreensReportsWithCpm,
        createCampaignProposalName,
        fetchCampaignProposalsByStatus,
        getCampaignProposalById,
        updateCampaignProposal,
        createLabel,
        getLabelsByCampaignId,
        deleteCampaignProposal,
        linkLabelAndSubcampaign,
        createCampaign,
        generateProposals,
        getLinkedCampaigns,
        getProposalBrief,
        getProposalsById,
        usoProposal,
        checkSubCampaignBeforeLaunch,
        sendEmailWithProposal,
        unassignLabelByCampaignId,
        getCampaignByUserIdAndStatus,
        getCampaignProposalCount,
        getCountriesByCurrencyId,
        getRegionsByCountryIds,
        getCitiesByRegionIds,
        getAreasByCityIds,
        uploadCreativeFile,
        createUrlCreatives,
        linkMultipleCreativesToScreens,
        adjustCampaignExpectedStats,
        saveCampaignExpectedStatsTargets,
    };
}
